import React, { useState, useMemo, useRef } from 'react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from 'recharts';
import { API_URL } from '../config';
import { situacaoExameLabel, situacaoExameStyle } from '../utils/situacaoExame';
import './DataPage.css';

const styles = {
  page: { backgroundColor: '#f5f5f5', minHeight: '100vh' },
  breadcrumb: { fontSize: '13px', color: '#999', marginBottom: '4px' },
  breadcrumbLink: { color: '#1976d2', textDecoration: 'none', cursor: 'pointer' },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '12px',
  },
  pageIcon: {
    fontSize: '1.5rem',
    lineHeight: 1,
  },
  pageTitle: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#1a1a2e',
    margin: '0 0 2px 0',
  },
  pageSubtitle: {
    fontSize: '0.85rem',
    color: '#666',
    margin: 0,
  },

  uploadCard: {
    backgroundColor: '#fff', borderRadius: '8px', padding: '32px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: '24px',
    textAlign: 'center',
  },
  dropZone: {
    border: '2px dashed #ccc', borderRadius: '8px', padding: '40px 24px',
    cursor: 'pointer', transition: 'all 0.2s',
  },
  dropZoneActive: {
    border: '2px dashed #1976d2', backgroundColor: '#e3f2fd',
  },
  dropIcon: { fontSize: '40px', marginBottom: '12px', color: '#999' },
  dropText: { fontSize: '16px', color: '#555', margin: '0 0 8px 0' },
  dropHint: { fontSize: '13px', color: '#999', margin: 0 },
  fileInput: { display: 'none' },

  cabecalhoCard: {
    backgroundColor: '#fff', borderRadius: '8px', padding: '20px 24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: '24px',
    display: 'flex', gap: '32px', alignItems: 'center', flexWrap: 'wrap',
  },
  cabInfo: { display: 'flex', flexDirection: 'column', gap: '2px' },
  cabLabel: { fontSize: '11px', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' },
  cabValue: { fontSize: '15px', fontWeight: '600', color: '#333' },

  summaryCards: { display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' },
  summaryCard: {
    backgroundColor: '#fff', borderRadius: '8px', padding: '16px 20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)', flex: '1', minWidth: '140px',
    borderLeft: '4px solid #ccc',
  },
  summaryLabel: { fontSize: '11px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' },
  summaryValue: { fontSize: '22px', fontWeight: '700', color: '#333' },

  filtersRow: {
    backgroundColor: '#fff', borderRadius: '8px', padding: '16px 20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: '16px',
    display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap',
  },
  filterGroup: { display: 'flex', flexDirection: 'column', minWidth: '140px' },
  filterLabel: { fontSize: '11px', color: '#666', marginBottom: '4px', fontWeight: '500' },
  input: {
    padding: '8px 10px', border: '1px solid #ddd', borderRadius: '6px',
    fontSize: '13px', color: '#333', outline: 'none', width: '100%', boxSizing: 'border-box',
  },
  select: {
    padding: '8px 10px', border: '1px solid #ddd', borderRadius: '6px',
    fontSize: '13px', color: '#333', outline: 'none', backgroundColor: '#fff',
    cursor: 'pointer', width: '100%', boxSizing: 'border-box',
  },
  exportBtn: {
    padding: '8px 16px', backgroundColor: '#2e7d32', color: '#fff', border: 'none',
    borderRadius: '6px', fontSize: '13px', fontWeight: '500', cursor: 'pointer',
    whiteSpace: 'nowrap', marginLeft: 'auto',
  },
  clearBtn: {
    padding: '8px 16px', backgroundColor: '#f5f5f5', color: '#666',
    border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px',
    cursor: 'pointer', whiteSpace: 'nowrap',
  },

  tableCard: {
    backgroundColor: '#fff', borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden',
  },
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px', tableLayout: 'fixed' },
  th: {
    padding: '12px 10px', textAlign: 'left', backgroundColor: '#f8f8f8',
    color: '#555', fontWeight: '600', borderBottom: '2px solid #eee',
    whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none',
  },
  td: { padding: '10px', borderBottom: '1px solid #f0f0f0', color: '#333' },
  tdRight: { padding: '10px', borderBottom: '1px solid #f0f0f0', color: '#333', textAlign: 'right', fontVariantNumeric: 'tabular-nums' },

  badge: {
    padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600',
    display: 'inline-block', whiteSpace: 'nowrap',
  },
  loading: { textAlign: 'center', padding: '40px', color: '#999' },
  empty: { textAlign: 'center', padding: '60px 20px', color: '#999' },
  tableInfo: { padding: '12px 16px', fontSize: '12px', color: '#999', borderTop: '1px solid #f0f0f0' },

  // Tabs
  tabs: { display: 'flex', gap: '0', marginBottom: '24px', borderBottom: '2px solid #e0e0e0' },
  tab: {
    padding: '12px 24px', fontSize: '14px', fontWeight: '500', cursor: 'pointer',
    color: '#666', backgroundColor: 'transparent', border: 'none', borderBottom: '2px solid transparent',
    marginBottom: '-2px', transition: 'all 0.2s',
  },
  tabActive: {
    color: '#1976d2', borderBottomColor: '#1976d2', fontWeight: '600',
  },

  // Relatorio styles
  section: {
    backgroundColor: '#fff', borderRadius: '8px', padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: '20px',
  },
  sectionTitle: { fontSize: '16px', fontWeight: '600', color: '#333', margin: '0 0 16px 0', paddingBottom: '10px', borderBottom: '1px solid #eee' },
  flowRow: { display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '12px' },
  flowBox: {
    padding: '12px 16px', borderRadius: '8px', fontSize: '13px',
    display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '140px',
  },
  flowArrow: { fontSize: '20px', color: '#999', fontWeight: 'bold' },
  mapTable: { width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginTop: '8px' },
  mapTh: { padding: '10px 12px', textAlign: 'left', backgroundColor: '#f8f8f8', color: '#555', fontWeight: '600', borderBottom: '2px solid #eee' },
  mapTd: { padding: '10px 12px', borderBottom: '1px solid #f0f0f0', color: '#333' },
  infoCard: {
    padding: '16px 20px', borderRadius: '8px', marginBottom: '12px',
    fontSize: '13px', lineHeight: '1.6',
  },

  // Divergencias tab
  priorityBadge: {
    padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600',
    display: 'inline-block', whiteSpace: 'nowrap',
  },
  checklistItem: {
    display: 'flex', alignItems: 'flex-start', gap: '16px', padding: '16px 20px',
    borderBottom: '1px solid #f0f0f0',
  },
  checklistNumber: {
    width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#1976d2',
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '14px', fontWeight: '700', flexShrink: 0,
  },
};

const STATUS_CONFIG = {
  OK: { label: 'OK', bg: '#e8f5e9', color: '#2e7d32' },
  DIVERGENCIA_VALOR: { label: 'Valor divergente', bg: '#fff3e0', color: '#e65100' },
  NAO_ENCONTRADO: { label: 'N/encontrado', bg: '#ffebee', color: '#c62828' },
  NAO_COBRADO: { label: 'N/cobrado UNIMED', bg: '#e3f2fd', color: '#1565c0' },
  FORA_PERIODO: { label: 'Fora do periodo', bg: '#f3e5f5', color: '#7b1fa2' },
};

function formatCurrency(val) {
  if (val == null) return '-';
  return val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDate(val) {
  if (!val) return '-';
  // Aceita "2026-01-15" ou "15/01/2026" e sempre retorna dd/mm/aaaa
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(val)) return val;
  const d = new Date(val);
  if (isNaN(d.getTime())) return val;
  return d.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
}

function formatCode(code) {
  if (!code || !code.includes('+')) return code || '-';
  return code.split('+').map((part, i, arr) => (
    <React.Fragment key={i}>{part}{i < arr.length - 1 ? <>+<wbr /></> : null}</React.Fragment>
  ));
}

function formatPercent(val, total) {
  if (!total) return '0%';
  return (val / total * 100).toFixed(1) + '%';
}

// ── Relatório Técnico ───────────────────────────────────────────────────

function RelatorioTecnico({ resultado }) {
  const { resumo, itens, cabecalho } = resultado;
  const totalMatched = resumo.ok + resumo.divergenciaValor;
  const taxaMatch = resumo.totalItensXML > 0 ? (totalMatched / resumo.totalItensXML * 100).toFixed(1) : 0;

  // Calcular dados agrupados dos combos
  const combos = useMemo(() => {
    return itens.filter(i => (i.descricaoProcedimento || '').includes('[COMBO]'));
  }, [itens]);

  const combosPorTipo = useMemo(() => {
    const map = {};
    for (const item of combos) {
      const desc = item.descricaoProcedimento.replace('[COMBO] ', '');
      const label = desc.split('(')[1]?.replace(')', '') || desc;
      const tipo = desc.split('(')[0]?.trim() || desc;
      if (!map[tipo]) map[tipo] = { tipo, label, count: 0, ok: 0, div: 0, valorXML: 0, valorDB: 0 };
      map[tipo].count++;
      if (item.status === 'OK') map[tipo].ok++;
      else map[tipo].div++;
      map[tipo].valorXML += item.valorXML || 0;
      map[tipo].valorDB += item.valorDB || 0;
    }
    return Object.values(map).sort((a, b) => b.count - a.count);
  }, [combos]);

  // Mapeamentos de código usados (itens onde código XML difere do DB)
  const codeMaps = useMemo(() => {
    const maps = [
      { xmlCode: '40307182', dbCode: '40301782', descXml: 'HIV1+HIV2 determinacao conjunta', descDb: 'HIV 1 E 2 (Hermes Pardini)', count: 0, valorXML: 0, valorDB: 0 },
      { xmlCode: '40313310', dbCode: '40313190', descXml: 'Cromo pesquisa', descDb: 'Cromo LabRede', count: 0, valorXML: 0, valorDB: 0 },
    ];
    for (const item of itens) {
      if (item.status === 'NAO_ENCONTRADO' || item.status === 'NAO_COBRADO' || item.status === 'FORA_PERIODO') continue;
      for (const m of maps) {
        if (item.codigoProcedimento === m.xmlCode) {
          m.count++;
          m.valorXML += item.valorXML || 0;
          m.valorDB += item.valorDB || 0;
        }
      }
    }
    return maps;
  }, [itens]);

  // Agrupar divergências por faixa de valor
  const divergencias = useMemo(() => {
    const divs = itens.filter(i => i.status === 'DIVERGENCIA_VALOR');
    const faixas = { menor1: 0, de1a5: 0, de5a20: 0, maior20: 0 };
    let totalDiff = 0;
    for (const d of divs) {
      const abs = Math.abs(d.diferenca);
      totalDiff += d.diferenca;
      if (abs < 1) faixas.menor1++;
      else if (abs < 5) faixas.de1a5++;
      else if (abs < 20) faixas.de5a20++;
      else faixas.maior20++;
    }
    return { total: divs.length, totalDiff: Math.round(totalDiff * 100) / 100, faixas };
  }, [itens]);

  // Top NE por código
  const topNE = useMemo(() => {
    const ne = itens.filter(i => i.status === 'NAO_ENCONTRADO');
    const map = {};
    for (const item of ne) {
      const k = item.codigoProcedimento;
      if (!map[k]) map[k] = { code: k, desc: item.descricaoProcedimento, count: 0, valor: 0 };
      map[k].count++;
      map[k].valor += item.valorXML;
    }
    return Object.values(map).sort((a, b) => b.count - a.count).slice(0, 10);
  }, [itens]);

  // Timing de faturamento: diferença de datas (XML UNIMED vs Sistema)
  const timing = useMemo(() => {
    const matched = itens.filter(i =>
      (i.status === 'OK' || i.status === 'DIVERGENCIA_VALOR') && i.dataRealizacao && i.dataDB
    );
    const diffs = matched.map(i => {
      const diffMs = new Date(i.dataRealizacao) - new Date(i.dataDB);
      return Math.round(diffMs / 86400000);
    });
    if (diffs.length === 0) return null;

    // Distribuição por dia
    const dist = {};
    for (const d of diffs) dist[d] = (dist[d] || 0) + 1;
    const chartData = Object.keys(dist)
      .map(Number)
      .sort((a, b) => a - b)
      .map(d => ({ dia: d, label: `${d >= 0 ? '+' : ''}${d}d`, qtd: dist[d] }));

    // Faixas agrupadas
    const exato = diffs.filter(d => d === 0).length;
    const uniAntes = diffs.filter(d => d >= -2 && d <= -1).length;
    const uniDepois = diffs.filter(d => d >= 1 && d <= 2).length;
    const ate5 = diffs.filter(d => Math.abs(d) >= 3 && Math.abs(d) <= 5).length;
    const maior5 = diffs.filter(d => Math.abs(d) > 5).length;

    const sorted = [...diffs].sort((a, b) => a - b);
    const avg = (sorted.reduce((s, v) => s + v, 0) / sorted.length);
    const median = sorted[Math.floor(sorted.length / 2)];

    const pieData = [
      { name: 'Mesma data', value: exato, color: '#4caf50' },
      { name: '1-2 dias antes', value: uniAntes, color: '#2196f3' },
      { name: '1-2 dias depois', value: uniDepois, color: '#ff9800' },
      { name: '3-5 dias', value: ate5, color: '#ff5722' },
      { name: '> 5 dias', value: maior5, color: '#9c27b0' },
    ].filter(d => d.value > 0);

    return { chartData, pieData, total: diffs.length, exato, uniAntes, uniDepois, ate5, maior5, avg, median };
  }, [itens]);

  // Top NC por código
  const topNC = useMemo(() => {
    const nc = itens.filter(i => i.status === 'NAO_COBRADO');
    const map = {};
    for (const item of nc) {
      const k = item.codigoProcedimento || '[Sem codigo TUSS]';
      if (!map[k]) map[k] = { code: k, desc: item.descricaoProcedimento, count: 0, valor: 0 };
      map[k].count++;
      map[k].valor += Math.abs(item.valorDB);
    }
    return Object.values(map).sort((a, b) => b.count - a.count).slice(0, 10);
  }, [itens]);

  return (
    <div>
      {/* ── Resumo Executivo ── */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Resumo Executivo</h3>
        <div style={{ ...styles.infoCard, backgroundColor: '#e8f5e9', border: '1px solid #c8e6c9' }}>
          <strong>Taxa de conciliacao: {taxaMatch}%</strong> ({totalMatched.toLocaleString()} de {resumo.totalItensXML.toLocaleString()} itens do demonstrativo foram identificados no sistema)
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px', marginTop: '12px' }}>
          <div style={{ ...styles.infoCard, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
            <strong>Demonstrativo #{cabecalho.numeroDemonstrativo}</strong><br />
            Operadora: {cabecalho.nomeOperadora}<br />
            Periodo coberto: {formatDate(resumo.periodoInicio)} a {formatDate(resumo.periodoFim)}<br />
            Valor total UNIMED: {formatCurrency(resumo.valorTotalXML)}<br />
            Valor conferido no sistema: {formatCurrency(resumo.valorTotalDB)}<br />
            Diferenca liquida: <span style={{ color: resumo.diferencaTotal >= 0 ? '#2e7d32' : '#c62828', fontWeight: '600' }}>{formatCurrency(resumo.diferencaTotal)}</span>
          </div>
          <div style={{ ...styles.infoCard, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
            <strong>Distribuicao dos itens XML ({resumo.totalItensXML})</strong><br />
            <span style={{ color: '#2e7d32' }}>Conferidos OK: {resumo.ok} ({formatPercent(resumo.ok, resumo.totalItensXML)})</span><br />
            <span style={{ color: '#e65100' }}>Valor divergente: {resumo.divergenciaValor} ({formatPercent(resumo.divergenciaValor, resumo.totalItensXML)})</span><br />
            <span style={{ color: '#c62828' }}>Nao encontrados: {resumo.naoEncontrado} ({formatPercent(resumo.naoEncontrado, resumo.totalItensXML)})</span><br />
            <span style={{ color: '#1565c0' }}>Nao cobrados UNIMED: {resumo.naoCobrado}</span> | <span style={{ color: '#7b1fa2' }}>Fora do periodo: {resumo.foraPeriodo || 0}</span>
          </div>
        </div>
      </div>

      {/* ── Como funciona a Conferência ── */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Como funciona a Conferencia Automatica</h3>

        <div style={{ ...styles.infoCard, backgroundColor: '#e3f2fd', border: '1px solid #bbdefb', marginBottom: '16px' }}>
          A UNIMED envia um <strong>XML padrao TISS</strong> com todos os exames que foram pagos no periodo.
          O sistema cruza automaticamente cada item do XML com os exames registrados no banco de dados,
          usando <strong>codigo do procedimento + carteirinha do paciente + data</strong> como criterios de identificacao.
        </div>

        <h4 style={{ fontSize: '14px', color: '#555', margin: '16px 0 12px' }}>Fluxo de processamento:</h4>

        <div style={styles.flowRow}>
          <div style={{ ...styles.flowBox, backgroundColor: '#e3f2fd', border: '1px solid #90caf9' }}>
            <strong>1. Upload XML</strong>
            <span style={{ fontSize: '12px', color: '#555' }}>Arquivo TISS da UNIMED</span>
          </div>
          <span style={styles.flowArrow}>&rarr;</span>
          <div style={{ ...styles.flowBox, backgroundColor: '#e8f5e9', border: '1px solid #a5d6a7' }}>
            <strong>2. Extrair itens</strong>
            <span style={{ fontSize: '12px', color: '#555' }}>Guias, procedimentos, valores</span>
          </div>
          <span style={styles.flowArrow}>&rarr;</span>
          <div style={{ ...styles.flowBox, backgroundColor: '#fff3e0', border: '1px solid #ffcc80' }}>
            <strong>3. Buscar no sistema</strong>
            <span style={{ fontSize: '12px', color: '#555' }}>UNIMED + periodo + margem</span>
          </div>
          <span style={styles.flowArrow}>&rarr;</span>
          <div style={{ ...styles.flowBox, backgroundColor: '#f3e5f5', border: '1px solid #ce93d8' }}>
            <strong>4. Cruzar dados</strong>
            <span style={{ fontSize: '12px', color: '#555' }}>Match inteligente por score</span>
          </div>
          <span style={styles.flowArrow}>&rarr;</span>
          <div style={{ ...styles.flowBox, backgroundColor: '#e8eaf6', border: '1px solid #9fa8da' }}>
            <strong>5. Classificar</strong>
            <span style={{ fontSize: '12px', color: '#555' }}>OK / Divergencia / N.E. / N.C.</span>
          </div>
        </div>

        <h4 style={{ fontSize: '14px', color: '#555', margin: '20px 0 12px' }}>Logica de identificacao (Match por Score):</h4>
        <div style={{ ...styles.infoCard, backgroundColor: '#fafafa', border: '1px solid #e0e0e0' }}>
          Para cada item do XML, o sistema busca no banco de dados um exame com o <strong>mesmo codigo de procedimento</strong> e calcula um <strong>score de confianca</strong>:<br /><br />
          <table style={{ ...styles.mapTable, maxWidth: '500px' }}>
            <thead>
              <tr>
                <th style={styles.mapTh}>Criterio</th>
                <th style={{ ...styles.mapTh, textAlign: 'center' }}>Pontos</th>
              </tr>
            </thead>
            <tbody>
              <tr><td style={styles.mapTd}>Codigo do procedimento igual</td><td style={{ ...styles.mapTd, textAlign: 'center' }}>+1 (base)</td></tr>
              <tr><td style={styles.mapTd}>Mesma data</td><td style={{ ...styles.mapTd, textAlign: 'center' }}>+5</td></tr>
              <tr><td style={styles.mapTd}>Data com 1 dia de diferenca</td><td style={{ ...styles.mapTd, textAlign: 'center' }}>+3</td></tr>
              <tr><td style={styles.mapTd}>Data com 2 dias de diferenca</td><td style={{ ...styles.mapTd, textAlign: 'center' }}>+2</td></tr>
              <tr><td style={styles.mapTd}>Data com 3-5 dias (so com carteirinha)</td><td style={{ ...styles.mapTd, textAlign: 'center' }}>+1</td></tr>
              <tr><td style={styles.mapTd}><strong>Carteirinha do paciente igual</strong></td><td style={{ ...styles.mapTd, textAlign: 'center' }}><strong>+10</strong></td></tr>
            </tbody>
          </table>
          <br />
          <strong>Score minimo para aceitar: 3 pontos.</strong> Isso garante que so faz match se houver carteirinha igual OU data muito proxima.<br />
          <strong>Tolerancia de data:</strong> Ate 2 dias sem carteirinha. Ate 5 dias com carteirinha confirmada.
        </div>

        <h4 style={{ fontSize: '14px', color: '#555', margin: '20px 0 12px' }}>Por que as datas podem ser diferentes?</h4>
        <div style={{ ...styles.infoCard, backgroundColor: '#fff8e1', border: '1px solid #ffe082' }}>
          A UNIMED usa a <strong>data de autorizacao/faturamento</strong> no demonstrativo, enquanto o sistema registra a <strong>data da visita/coleta</strong> do paciente.
          Essa diferenca eh tipicamente de 1-2 dias, mas pode chegar a 5 dias em casos de feriados ou atrasos na autorizacao.
        </div>
      </div>

      {/* ── Status de classificação ── */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>O que significa cada status</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px' }}>
          <div style={{ ...styles.infoCard, backgroundColor: '#e8f5e9', border: '1px solid #c8e6c9' }}>
            <span style={{ ...styles.badge, backgroundColor: '#e8f5e9', color: '#2e7d32', border: '1px solid #a5d6a7', marginBottom: '8px' }}>OK</span><br />
            O exame foi encontrado no sistema com o <strong>mesmo valor</strong> que a UNIMED pagou. Conferencia perfeita.
          </div>
          <div style={{ ...styles.infoCard, backgroundColor: '#fff3e0', border: '1px solid #ffcc80' }}>
            <span style={{ ...styles.badge, backgroundColor: '#fff3e0', color: '#e65100', border: '1px solid #ffcc80', marginBottom: '8px' }}>Valor divergente</span><br />
            O exame foi encontrado, mas o <strong>valor</strong> que a UNIMED pagou eh diferente do que esta no sistema. Necessita verificacao.
          </div>
          <div style={{ ...styles.infoCard, backgroundColor: '#ffebee', border: '1px solid #ef9a9a' }}>
            <span style={{ ...styles.badge, backgroundColor: '#ffebee', color: '#c62828', border: '1px solid #ef9a9a', marginBottom: '8px' }}>N/encontrado</span><br />
            A UNIMED cobrou este exame no demonstrativo, mas o sistema <strong>nao encontrou correspondencia</strong> no banco de dados (data/paciente nao batem).
          </div>
          <div style={{ ...styles.infoCard, backgroundColor: '#e3f2fd', border: '1px solid #90caf9' }}>
            <span style={{ ...styles.badge, backgroundColor: '#e3f2fd', color: '#1565c0', border: '1px solid #90caf9', marginBottom: '8px' }}>N/cobrado UNIMED</span><br />
            O exame existe no sistema (dentro do periodo), mas a UNIMED <strong>nao incluiu</strong> no demonstrativo de pagamento. Pode ser glosa ou inclusao futura.
          </div>
          <div style={{ ...styles.infoCard, backgroundColor: '#f3e5f5', border: '1px solid #ce93d8' }}>
            <span style={{ ...styles.badge, backgroundColor: '#f3e5f5', color: '#7b1fa2', border: '1px solid #ce93d8', marginBottom: '8px' }}>Fora do periodo</span><br />
            O exame existe no sistema mas com data <strong>fora do periodo</strong> coberto pelo demonstrativo. Eh esperado nao estar incluido.
          </div>
        </div>
      </div>

      {/* ── Mapeamentos de Código ── */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Mapeamento de Codigos (CODE_MAP)</h3>
        <div style={{ ...styles.infoCard, backgroundColor: '#e3f2fd', border: '1px solid #bbdefb', marginBottom: '16px' }}>
          Alguns exames usam <strong>codigos TUSS diferentes</strong> no XML da UNIMED e no sistema interno, apesar de serem o mesmo exame.
          O sistema traduz automaticamente esses codigos para fazer o match correto.
        </div>
        <table style={styles.mapTable}>
          <thead>
            <tr>
              <th style={styles.mapTh}>Codigo UNIMED (XML)</th>
              <th style={styles.mapTh}>Codigo Sistema (DB)</th>
              <th style={styles.mapTh}>Exame</th>
              <th style={{ ...styles.mapTh, textAlign: 'right' }}>Qtd matched</th>
              <th style={{ ...styles.mapTh, textAlign: 'right' }}>Valor XML</th>
            </tr>
          </thead>
          <tbody>
            {codeMaps.map((m, i) => (
              <tr key={i} style={{ backgroundColor: i % 2 ? '#fafafa' : '#fff' }}>
                <td style={{ ...styles.mapTd, fontFamily: 'monospace' }}>{m.xmlCode}</td>
                <td style={{ ...styles.mapTd, fontFamily: 'monospace' }}>{m.dbCode}</td>
                <td style={styles.mapTd}>{m.descXml} &rarr; {m.descDb}</td>
                <td style={{ ...styles.mapTd, textAlign: 'right' }}>{m.count}</td>
                <td style={{ ...styles.mapTd, textAlign: 'right' }}>{formatCurrency(m.valorXML)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Exames Combo ── */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Exames Combo (COMBO_MAP) - {combos.length} matches encontrados</h3>
        <div style={{ ...styles.infoCard, backgroundColor: '#fff3e0', border: '1px solid #ffcc80', marginBottom: '16px' }}>
          Alguns exames no sistema sao armazenados como <strong>um unico item</strong> (ex: "Colesterol Total/Fracoes + Triglicerideos"),
          mas a UNIMED desmembra em <strong>multiplos codigos TUSS individuais</strong> (ex: HDL + Triglicerideos + Colesterol Total).
          O sistema identifica esses combos e soma os valores dos componentes para comparar com o valor unico do sistema.
        </div>
        <table style={styles.mapTable}>
          <thead>
            <tr>
              <th style={styles.mapTh}>Exame no Sistema</th>
              <th style={styles.mapTh}>Codigos UNIMED</th>
              <th style={{ ...styles.mapTh, textAlign: 'center' }}>Qtd</th>
              <th style={{ ...styles.mapTh, textAlign: 'center' }}>OK</th>
              <th style={{ ...styles.mapTh, textAlign: 'center' }}>Div.</th>
              <th style={{ ...styles.mapTh, textAlign: 'right' }}>Val. XML</th>
              <th style={{ ...styles.mapTh, textAlign: 'right' }}>Val. Sistema</th>
            </tr>
          </thead>
          <tbody>
            {combosPorTipo.map((c, i) => (
              <tr key={i} style={{ backgroundColor: i % 2 ? '#fafafa' : '#fff' }}>
                <td style={styles.mapTd}>
                  <strong>{c.tipo}</strong>
                  <div style={{ fontSize: '11px', color: '#888' }}>{c.label}</div>
                </td>
                <td style={{ ...styles.mapTd, fontSize: '11px', fontFamily: 'monospace' }}>
                  {/* Extrair códigos dos itens matched */}
                  {combos.filter(x => x.descricaoProcedimento.includes(c.tipo)).slice(0, 1).map(x => x.codigoProcedimento).join(', ')}
                </td>
                <td style={{ ...styles.mapTd, textAlign: 'center', fontWeight: '600' }}>{c.count}</td>
                <td style={{ ...styles.mapTd, textAlign: 'center', color: '#2e7d32' }}>{c.ok}</td>
                <td style={{ ...styles.mapTd, textAlign: 'center', color: '#e65100' }}>{c.div}</td>
                <td style={{ ...styles.mapTd, textAlign: 'right' }}>{formatCurrency(c.valorXML)}</td>
                <td style={{ ...styles.mapTd, textAlign: 'right' }}>{formatCurrency(c.valorDB)}</td>
              </tr>
            ))}
            {combosPorTipo.length === 0 && (
              <tr><td colSpan={7} style={{ ...styles.mapTd, textAlign: 'center', color: '#999' }}>Nenhum combo identificado neste demonstrativo</td></tr>
            )}
          </tbody>
        </table>
        {combosPorTipo.length > 0 && (
          <div style={{ padding: '12px 0 0', fontSize: '12px', color: '#666' }}>
            Total de combos matched: <strong>{combos.length}</strong> |
            Valor XML: <strong>{formatCurrency(combos.reduce((s, c) => s + (c.valorXML || 0), 0))}</strong> |
            Valor Sistema: <strong>{formatCurrency(combos.reduce((s, c) => s + (c.valorDB || 0), 0))}</strong>
          </div>
        )}
      </div>

      {/* ── Divergências ── */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Analise das Divergencias de Valor ({divergencias.total})</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' }}>
          <div style={{ ...styles.infoCard, backgroundColor: '#fff3e0', border: '1px solid #ffcc80' }}>
            <strong>Diferenca liquida total</strong><br />
            <span style={{ fontSize: '18px', fontWeight: '700', color: divergencias.totalDiff >= 0 ? '#2e7d32' : '#c62828' }}>
              {formatCurrency(divergencias.totalDiff)}
            </span>
          </div>
          <div style={{ ...styles.infoCard, backgroundColor: '#fafafa', border: '1px solid #e0e0e0' }}>
            <strong>Distribuicao por faixa</strong><br />
            &lt; R$ 1,00: {divergencias.faixas.menor1} itens (arredondamento)<br />
            R$ 1 a 5: {divergencias.faixas.de1a5} itens<br />
            R$ 5 a 20: {divergencias.faixas.de5a20} itens<br />
            &gt; R$ 20: {divergencias.faixas.maior20} itens (verificar)
          </div>
        </div>
      </div>

      {/* ── Top N/Encontrado ── */}
      {topNE.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Top 10 - Nao Encontrados no Sistema ({resumo.naoEncontrado})</h3>
          <div style={{ ...styles.infoCard, backgroundColor: '#ffebee', border: '1px solid #ef9a9a', marginBottom: '16px' }}>
            Itens que a UNIMED incluiu no demonstrativo mas que o sistema nao conseguiu identificar.
            Possivel causa: paciente atendido mas com diferenca de data maior que 5 dias, ou exame registrado com outro convenio.
          </div>
          <table style={styles.mapTable}>
            <thead>
              <tr>
                <th style={styles.mapTh}>Codigo</th>
                <th style={styles.mapTh}>Descricao</th>
                <th style={{ ...styles.mapTh, textAlign: 'center' }}>Qtd</th>
                <th style={{ ...styles.mapTh, textAlign: 'right' }}>Valor total</th>
              </tr>
            </thead>
            <tbody>
              {topNE.map((item, i) => (
                <tr key={i} style={{ backgroundColor: i % 2 ? '#fafafa' : '#fff' }}>
                  <td style={{ ...styles.mapTd, fontFamily: 'monospace' }}>{item.code}</td>
                  <td style={{ ...styles.mapTd, fontSize: '12px' }}>{item.desc.substring(0, 80)}</td>
                  <td style={{ ...styles.mapTd, textAlign: 'center' }}>{item.count}</td>
                  <td style={{ ...styles.mapTd, textAlign: 'right' }}>{formatCurrency(item.valor)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Top N/Cobrado ── */}
      {topNC.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Top 10 - Nao Cobrados pela UNIMED ({resumo.naoCobrado})</h3>
          <div style={{ ...styles.infoCard, backgroundColor: '#e3f2fd', border: '1px solid #90caf9', marginBottom: '16px' }}>
            Exames que existem no sistema (dentro do periodo do demonstrativo) mas a UNIMED nao incluiu no pagamento.
            Podem ser: glosas nao comunicadas, itens que serao incluidos em outro demonstrativo, ou exames cancelados.
          </div>
          <table style={styles.mapTable}>
            <thead>
              <tr>
                <th style={styles.mapTh}>Codigo</th>
                <th style={styles.mapTh}>Descricao</th>
                <th style={{ ...styles.mapTh, textAlign: 'center' }}>Qtd</th>
                <th style={{ ...styles.mapTh, textAlign: 'right' }}>Valor total</th>
              </tr>
            </thead>
            <tbody>
              {topNC.map((item, i) => (
                <tr key={i} style={{ backgroundColor: i % 2 ? '#fafafa' : '#fff' }}>
                  <td style={{ ...styles.mapTd, fontFamily: 'monospace' }}>{item.code}</td>
                  <td style={{ ...styles.mapTd, fontSize: '12px' }}>{item.desc.substring(0, 80)}</td>
                  <td style={{ ...styles.mapTd, textAlign: 'center' }}>{item.count}</td>
                  <td style={{ ...styles.mapTd, textAlign: 'right' }}>{formatCurrency(item.valor)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Barra de progresso visual ── */}
      <div style={styles.section}>
        {/* ── Timing de Faturamento ── */}
        {timing && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Timing de Faturamento: UNIMED vs Sistema</h3>
            <div style={{ ...styles.infoCard, backgroundColor: '#e3f2fd', border: '1px solid #bbdefb', marginBottom: '20px' }}>
              Comparacao entre a <strong>data que a UNIMED registra</strong> (autorizacao/faturamento) e a <strong>data do sistema</strong> (coleta/visita do paciente).
              Valores negativos significam que a UNIMED registrou <strong>antes</strong> do sistema, positivos significam <strong>depois</strong>.
              Baseado em <strong>{timing.total.toLocaleString()}</strong> itens conferidos.
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '20px', marginBottom: '20px' }}>
              {/* Histograma */}
              <div>
                <h4 style={{ fontSize: '13px', color: '#555', margin: '0 0 12px', fontWeight: '600' }}>Distribuicao da diferenca de datas (dias)</h4>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={timing.chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#666' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#666' }} />
                    <Tooltip
                      formatter={(val) => [`${val} itens`, 'Quantidade']}
                      labelFormatter={(label) => `Diferenca: ${label}`}
                      contentStyle={{ fontSize: '12px', borderRadius: '8px' }}
                    />
                    <Bar dataKey="qtd" radius={[4, 4, 0, 0]}>
                      {timing.chartData.map((entry, i) => (
                        <Cell
                          key={i}
                          fill={
                            entry.dia === 0 ? '#4caf50' :
                            Math.abs(entry.dia) <= 2 ? '#2196f3' :
                            Math.abs(entry.dia) <= 5 ? '#ff9800' : '#f44336'
                          }
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Pizza + Stats */}
              <div>
                <h4 style={{ fontSize: '13px', color: '#555', margin: '0 0 8px', fontWeight: '600' }}>Composicao</h4>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie
                      data={timing.pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {timing.pieData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(val, name) => [`${val} (${(val / timing.total * 100).toFixed(1)}%)`, name]}
                      contentStyle={{ fontSize: '12px', borderRadius: '8px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', marginTop: '4px' }}>
                  {timing.pieData.map((d, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ width: '10px', height: '10px', borderRadius: '2px', backgroundColor: d.color, flexShrink: 0 }}></span>
                      <span style={{ color: '#555' }}>{d.name}</span>
                      <span style={{ marginLeft: 'auto', fontWeight: '600', color: '#333' }}>{d.value} ({(d.value / timing.total * 100).toFixed(1)}%)</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Cards de métricas */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
              <div style={{ ...styles.infoCard, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0', textAlign: 'center' }}>
                <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Media</div>
                <div style={{ fontSize: '22px', fontWeight: '700', color: '#333' }}>{timing.avg >= 0 ? '+' : ''}{timing.avg.toFixed(1)}d</div>
              </div>
              <div style={{ ...styles.infoCard, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0', textAlign: 'center' }}>
                <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Mediana</div>
                <div style={{ fontSize: '22px', fontWeight: '700', color: '#333' }}>{timing.median >= 0 ? '+' : ''}{timing.median}d</div>
              </div>
              <div style={{ ...styles.infoCard, backgroundColor: '#e8f5e9', border: '1px solid #c8e6c9', textAlign: 'center' }}>
                <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Mesma data</div>
                <div style={{ fontSize: '22px', fontWeight: '700', color: '#2e7d32' }}>{(timing.exato / timing.total * 100).toFixed(1)}%</div>
                <div style={{ fontSize: '11px', color: '#666' }}>{timing.exato} itens</div>
              </div>
              <div style={{ ...styles.infoCard, backgroundColor: '#e3f2fd', border: '1px solid #bbdefb', textAlign: 'center' }}>
                <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Ate 2 dias</div>
                <div style={{ fontSize: '22px', fontWeight: '700', color: '#1565c0' }}>{((timing.exato + timing.uniAntes + timing.uniDepois) / timing.total * 100).toFixed(1)}%</div>
                <div style={{ fontSize: '11px', color: '#666' }}>{timing.exato + timing.uniAntes + timing.uniDepois} itens</div>
              </div>
              <div style={{ ...styles.infoCard, backgroundColor: '#fff3e0', border: '1px solid #ffcc80', textAlign: 'center' }}>
                <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>3-5 dias</div>
                <div style={{ fontSize: '22px', fontWeight: '700', color: '#e65100' }}>{timing.ate5}</div>
                <div style={{ fontSize: '11px', color: '#666' }}>{(timing.ate5 / timing.total * 100).toFixed(1)}%</div>
              </div>
              <div style={{ ...styles.infoCard, backgroundColor: '#ffebee', border: '1px solid #ef9a9a', textAlign: 'center' }}>
                <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>&gt; 5 dias</div>
                <div style={{ fontSize: '22px', fontWeight: '700', color: '#c62828' }}>{timing.maior5}</div>
                <div style={{ fontSize: '11px', color: '#666' }}>{(timing.maior5 / timing.total * 100).toFixed(1)}%</div>
              </div>
            </div>

            <div style={{ ...styles.infoCard, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0', marginTop: '16px' }}>
              <strong>Conclusao:</strong> {timing.exato / timing.total > 0.6
                ? `A grande maioria (${(timing.exato / timing.total * 100).toFixed(0)}%) dos itens tem a mesma data no sistema e na UNIMED. `
                : 'Há uma variação significativa nas datas entre sistema e UNIMED. '}
              {((timing.exato + timing.uniAntes + timing.uniDepois) / timing.total * 100).toFixed(0)}% dos itens estao dentro de 2 dias de diferenca.
              {timing.maior5 > 0 ? ` Apenas ${timing.maior5} itens (${(timing.maior5 / timing.total * 100).toFixed(1)}%) tem diferenca superior a 5 dias.` : ''}
            </div>
          </div>
        )}

        <h3 style={styles.sectionTitle}>Visao Geral da Conciliacao</h3>
        <div style={{ height: '40px', borderRadius: '8px', overflow: 'hidden', display: 'flex', marginBottom: '12px', border: '1px solid #e0e0e0' }}>
          {resumo.ok > 0 && (
            <div style={{ width: `${resumo.ok / resumo.totalItensXML * 100}%`, backgroundColor: '#4caf50', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '11px', fontWeight: '600', minWidth: resumo.ok > 50 ? '40px' : '0' }}>
              {resumo.ok > 50 ? `${resumo.ok}` : ''}
            </div>
          )}
          {resumo.divergenciaValor > 0 && (
            <div style={{ width: `${resumo.divergenciaValor / resumo.totalItensXML * 100}%`, backgroundColor: '#ff9800', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '11px', fontWeight: '600', minWidth: resumo.divergenciaValor > 50 ? '40px' : '0' }}>
              {resumo.divergenciaValor > 50 ? `${resumo.divergenciaValor}` : ''}
            </div>
          )}
          {resumo.naoEncontrado > 0 && (
            <div style={{ width: `${resumo.naoEncontrado / resumo.totalItensXML * 100}%`, backgroundColor: '#f44336', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '11px', fontWeight: '600', minWidth: resumo.naoEncontrado > 20 ? '30px' : '0' }}>
              {resumo.naoEncontrado > 20 ? `${resumo.naoEncontrado}` : ''}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '20px', fontSize: '12px', color: '#666', flexWrap: 'wrap' }}>
          <span><span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#4caf50', borderRadius: '2px', marginRight: '4px', verticalAlign: 'middle' }}></span> OK ({resumo.ok})</span>
          <span><span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#ff9800', borderRadius: '2px', marginRight: '4px', verticalAlign: 'middle' }}></span> Divergencia ({resumo.divergenciaValor})</span>
          <span><span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#f44336', borderRadius: '2px', marginRight: '4px', verticalAlign: 'middle' }}></span> N/encontrado ({resumo.naoEncontrado})</span>
        </div>
      </div>
    </div>
  );
}

// ── Divergências ──────────────────────────────────────────────────────────


const COMBO_CODES = {
  'COLEST.TOTAL/FRAC': { codigos: ['40301583', '40302547', '40301605'], label: 'Colesterol Total/Frações + Triglicerídeos' },
  'COVID-19 E INFLUENZA': { codigos: ['40325024', '40324796', '40306232', '40306216', '40306224', '40306240'], label: 'COVID-19 + Influenza A/B' },
  'CITOMEGALOVIRUS IGM': { codigos: ['40306674'], label: 'Citomegalovírus IGM' },
  'CLOZAPINA': { codigos: ['40321754'], label: 'Clozapina dosagem' },
  'TESTE DO PEZINHO': { codigos: ['40304299', '40307700', '40304647', '40310345', '40316548', '40301672', '40301974', '40306488', '40307832', '40302563', '40304817', '40311317', '40316017', '40304850', '40306674'], label: 'Teste do Pezinho Master' },
};

// All combo TUSS codes flattened for quick lookup
const ALL_COMBO_TUSS = new Set(Object.values(COMBO_CODES).flatMap(c => c.codigos));

function DivergenciasTab({ resultado }) {
  const { resumo, itens } = resultado;

  const [divFiltroDir, setDivFiltroDir] = useState('todos');
  const [divFiltroFaixa, setDivFiltroFaixa] = useState('todos');
  const [neAgrupar, setNeAgrupar] = useState('guia');
  const [neCollapsed, setNeCollapsed] = useState({});
  const [ncCollapsed, setNcCollapsed] = useState({});
  const [ncFiltro, setNcFiltro] = useState('todos'); // todos | entregues | semTuss | pendentes
  const [secaoCollapsed, setSecaoCollapsed] = useState({});

  // ── 1. Impacto Financeiro ──
  const impactoFinanceiro = useMemo(() => {
    let countDiv = 0, contraLab = 0, favorLab = 0, valorNE = 0, valorNC = 0;
    for (const item of itens) {
      if (item.status === 'DIVERGENCIA_VALOR') {
        countDiv++;
        if (item.diferenca < 0) contraLab += Math.abs(item.diferenca);
        else favorLab += item.diferenca;
      } else if (item.status === 'NAO_ENCONTRADO') {
        valorNE += item.valorXML || 0;
      } else if (item.status === 'NAO_COBRADO') {
        valorNC += Math.abs(item.valorDB || 0);
      }
    }
    const totalDivergencias = countDiv + resumo.naoEncontrado + resumo.naoCobrado;
    const exposicaoLiquida = contraLab + valorNC - favorLab;
    return { totalDivergencias, countDiv, contraLab, favorLab, valorNE, valorNC, exposicaoLiquida };
  }, [itens, resumo]);

  // ── 2. Análise de Divergências de Valor por Procedimento ──
  const analiseValores = useMemo(() => {
    const divs = itens.filter(i => i.status === 'DIVERGENCIA_VALOR');
    const byCode = {};
    for (const item of divs) {
      const code = item.codigoProcedimento || '?';
      if (!byCode[code]) byCode[code] = { code, desc: item.descricaoProcedimento, count: 0, totalDiff: 0, totalXML: 0, totalDB: 0, diffs: [] };
      byCode[code].count++;
      byCode[code].totalDiff += item.diferenca;
      byCode[code].totalXML += item.valorXML || 0;
      byCode[code].totalDB += item.valorDB || 0;
      byCode[code].diffs.push(item.diferenca);
    }
    const procedimentos = Object.values(byCode).map(p => {
      const avgDiff = p.totalDiff / p.count;
      const isConsistent = p.count >= 2 && p.diffs.every(d => Math.abs(d - p.diffs[0]) < 0.50);
      let faixa;
      const absAvg = Math.abs(avgDiff);
      if (absAvg < 1) faixa = 'arredondamento';
      else if (absAvg < 5) faixa = 'pequena';
      else if (absAvg < 20) faixa = 'media';
      else faixa = 'grande';
      return { ...p, avgDiff, isConsistent, faixa };
    });
    procedimentos.sort((a, b) => Math.abs(b.totalDiff) - Math.abs(a.totalDiff));
    const sistematicos = procedimentos.filter(p => p.isConsistent).length;
    return { procedimentos, sistematicos };
  }, [itens]);

  const procFiltrados = useMemo(() => {
    let arr = analiseValores.procedimentos;
    if (divFiltroDir === 'menos') arr = arr.filter(p => p.totalDiff < 0);
    else if (divFiltroDir === 'mais') arr = arr.filter(p => p.totalDiff > 0);
    if (divFiltroFaixa !== 'todos') arr = arr.filter(p => p.faixa === divFiltroFaixa);
    return arr;
  }, [analiseValores, divFiltroDir, divFiltroFaixa]);

  const chartDataValores = useMemo(() => {
    return analiseValores.procedimentos.slice(0, 10).map(p => ({
      name: p.code,
      desc: (p.desc || '').substring(0, 40),
      valor: Math.round(p.totalDiff * 100) / 100,
    }));
  }, [analiseValores]);

  // ── 3. Análise de Não Encontrados ──
  const analiseNE = useMemo(() => {
    const ne = itens.filter(i => i.status === 'NAO_ENCONTRADO');
    const valorTotal = ne.reduce((s, i) => s + (i.valorXML || 0), 0);

    // Por guia
    const byGuia = {};
    for (const item of ne) {
      const g = item.numeroGuia || '(Sem guia)';
      if (!byGuia[g]) byGuia[g] = { guia: g, paciente: item.paciente || '(Sem nome)', itens: [], valor: 0 };
      byGuia[g].itens.push(item);
      byGuia[g].valor += item.valorXML || 0;
      if (item.paciente && byGuia[g].paciente === '(Sem nome)') byGuia[g].paciente = item.paciente;
    }
    const guias = Object.values(byGuia).sort((a, b) => b.valor - a.valor);

    // Por procedimento
    const byProc = {};
    for (const item of ne) {
      const k = item.codigoProcedimento || '?';
      if (!byProc[k]) byProc[k] = { code: k, desc: item.descricaoProcedimento, count: 0, valor: 0 };
      byProc[k].count++;
      byProc[k].valor += item.valorXML || 0;
    }
    const procedimentos = Object.values(byProc).sort((a, b) => b.valor - a.valor).slice(0, 15);

    // Combo órfãos
    const comboOrfaos = ne.filter(i => ALL_COMBO_TUSS.has(i.codigoProcedimento));
    const comboOrfaosValor = comboOrfaos.reduce((s, i) => s + (i.valorXML || 0), 0);

    // Com/sem nome
    const comNome = ne.filter(i => i.paciente && i.paciente !== '(Sem nome CSV)').length;
    const semNome = ne.length - comNome;

    return { total: ne.length, valorTotal, guias, procedimentos, comboOrfaos: comboOrfaos.length, comboOrfaosValor, comNome, semNome };
  }, [itens]);

  // ── 4. Análise de Não Cobrados ──
  const analiseNC = useMemo(() => {
    const nc = itens.filter(i => i.status === 'NAO_COBRADO');
    const valorTotal = nc.reduce((s, i) => s + Math.abs(i.valorDB || 0), 0);

    // Por paciente
    const byPac = {};
    for (const item of nc) {
      const p = item.paciente || '(Sem paciente)';
      if (!byPac[p]) byPac[p] = { paciente: p, itens: [], valor: 0, protocolo: item.protocolo };
      byPac[p].itens.push(item);
      byPac[p].valor += Math.abs(item.valorDB || 0);
    }
    const pacientes = Object.values(byPac).sort((a, b) => b.valor - a.valor);

    // Por procedimento
    const byProc = {};
    for (const item of nc) {
      const k = item.codigoProcedimento || '[Sem codigo TUSS]';
      if (!byProc[k]) byProc[k] = { code: k, desc: item.descricaoProcedimento, count: 0, valor: 0 };
      byProc[k].count++;
      byProc[k].valor += Math.abs(item.valorDB || 0);
    }

    // Por situação
    const bySituacao = {};
    for (const item of nc) {
      const s = item.situacaoExame || '?';
      if (!bySituacao[s]) bySituacao[s] = { situacao: s, count: 0, valor: 0 };
      bySituacao[s].count++;
      bySituacao[s].valor += Math.abs(item.valorDB || 0);
    }
    const situacoes = Object.values(bySituacao).sort((a, b) => b.count - a.count);
    const pieData = situacoes.map(s => {
      const st = situacaoExameStyle(s.situacao);
      return { name: situacaoExameLabel(s.situacao), value: s.count, color: st.color, valor: s.valor };
    });

    // Sem código TUSS
    const semTuss = nc.filter(i => !i.codigoProcedimento || i.codigoProcedimento === '[Sem codigo TUSS]');
    const semTussValor = semTuss.reduce((s, i) => s + Math.abs(i.valorDB || 0), 0);

    // Entregues/Impressos não pagos
    const entregues = nc.filter(i => i.situacaoExame === 'I' || i.situacaoExame === 'E');
    const entreguesValor = entregues.reduce((s, i) => s + Math.abs(i.valorDB || 0), 0);

    // Pendentes
    const pendentes = nc.filter(i => i.situacaoExame === 'P');
    const pendentesValor = pendentes.reduce((s, i) => s + Math.abs(i.valorDB || 0), 0);

    return { total: nc.length, valorTotal, pacientes, situacoes, pieData, semTuss: semTuss.length, semTussValor, entregues: entregues.length, entreguesValor, pendentes: pendentes.length, pendentesValor };
  }, [itens]);

  // NC filtrado por callout
  const ncPacientesFiltrados = useMemo(() => {
    if (ncFiltro === 'todos') return analiseNC.pacientes;
    const nc = itens.filter(i => i.status === 'NAO_COBRADO');
    let filtered;
    if (ncFiltro === 'entregues') filtered = nc.filter(i => i.situacaoExame === 'I' || i.situacaoExame === 'E');
    else if (ncFiltro === 'semTuss') filtered = nc.filter(i => !i.codigoProcedimento || i.codigoProcedimento === '[Sem codigo TUSS]');
    else if (ncFiltro === 'pendentes') filtered = nc.filter(i => i.situacaoExame === 'P');
    else filtered = nc;
    // Reagrupar por paciente
    const byPac = {};
    for (const item of filtered) {
      const p = item.paciente || '(Sem paciente)';
      if (!byPac[p]) byPac[p] = { paciente: p, itens: [], valor: 0, protocolo: item.protocolo };
      byPac[p].itens.push(item);
      byPac[p].valor += Math.abs(item.valorDB || 0);
    }
    return Object.values(byPac).sort((a, b) => b.valor - a.valor);
  }, [ncFiltro, analiseNC.pacientes, itens]);

  // ── 5. Resumo de Ações ──
  const resumoAcoes = useMemo(() => {
    const acoes = [];
    if (analiseValores.sistematicos > 0) {
      const impacto = analiseValores.procedimentos.filter(p => p.isConsistent).reduce((s, p) => s + Math.abs(p.totalDiff), 0);
      acoes.push({
        titulo: 'Atualizar tabela de precos',
        descricao: `${analiseValores.sistematicos} procedimento(s) com divergencia sistematica (mesma diferenca em todos os itens). Indica tabela de precos desatualizada no sistema.`,
        impacto, prioridade: 'alta',
      });
    }
    if (analiseNC.entregues > 0) {
      acoes.push({
        titulo: 'Contestar glosas silenciosas',
        descricao: `${analiseNC.entregues} exame(s) ja entregues/impressos mas nao incluidos no pagamento da UNIMED. Contatar UNIMED para verificar motivo.`,
        impacto: analiseNC.entreguesValor, prioridade: 'alta',
      });
    }
    if (analiseNE.total > 0) {
      acoes.push({
        titulo: 'Investigar itens nao encontrados',
        descricao: `${analiseNE.total} item(ns) do demonstrativo sem correspondencia no sistema. Verificar se foram registrados com outro convenio ou data diferente.`,
        impacto: analiseNE.valorTotal, prioridade: 'media',
      });
    }
    if (analiseNC.semTuss > 0) {
      acoes.push({
        titulo: 'Cadastrar codigos TUSS faltantes',
        descricao: `${analiseNC.semTuss} exame(s) sem codigo TUSS no sistema. Necessario cadastrar para que a conferencia automatica funcione corretamente.`,
        impacto: analiseNC.semTussValor, prioridade: 'media',
      });
    }
    if (analiseNC.pendentes > 0) {
      acoes.push({
        titulo: 'Aguardar liberacao de exames pendentes',
        descricao: `${analiseNC.pendentes} exame(s) com situacao "Pendente". Esses exames ainda nao foram liberados e podem ser incluidos em demonstrativos futuros.`,
        impacto: analiseNC.pendentesValor, prioridade: 'baixa',
      });
    }
    return acoes;
  }, [analiseValores, analiseNE, analiseNC]);

  // ── Export helpers ──
  const exportDivergenciasCSV = () => {
    const divs = itens.filter(i => i.status === 'DIVERGENCIA_VALOR');
    if (!divs.length) return;
    const headers = ['Codigo', 'Descricao', 'Guia', 'Paciente', 'Protocolo', 'Valor XML', 'Valor Sistema', 'Diferenca', 'Data'];
    const rows = divs.map(i => [
      i.codigoProcedimento, `"${(i.descricaoProcedimento || '').replace(/"/g, '""')}"`,
      i.numeroGuia, `"${(i.paciente || '').replace(/"/g, '""')}"`, i.protocolo,
      (i.valorXML || 0).toFixed(2), (i.valorDB || 0).toFixed(2), (i.diferenca || 0).toFixed(2), formatDate(i.dataRealizacao),
    ]);
    downloadCSV(headers, rows, 'divergencias_valor');
  };

  const exportNECSV = () => {
    const ne = itens.filter(i => i.status === 'NAO_ENCONTRADO');
    if (!ne.length) return;
    const headers = ['Guia', 'Codigo', 'Descricao', 'Paciente', 'Valor XML', 'Data'];
    const rows = ne.map(i => [
      i.numeroGuia, i.codigoProcedimento, `"${(i.descricaoProcedimento || '').replace(/"/g, '""')}"`,
      `"${(i.paciente || '').replace(/"/g, '""')}"`, (i.valorXML || 0).toFixed(2), formatDate(i.dataRealizacao),
    ]);
    downloadCSV(headers, rows, 'nao_encontrados');
  };

  const exportNCCSV = () => {
    const nc = itens.filter(i => i.status === 'NAO_COBRADO');
    if (!nc.length) return;
    const headers = ['Paciente', 'Protocolo', 'Codigo', 'Descricao', 'Exame', 'Situacao', 'Valor Sistema', 'Data'];
    const rows = nc.map(i => [
      `"${(i.paciente || '').replace(/"/g, '""')}"`, i.protocolo, i.codigoProcedimento,
      `"${(i.descricaoProcedimento || '').replace(/"/g, '""')}"`, `"${(i.exameNome || '').replace(/"/g, '""')}"`,
      situacaoExameLabel(i.situacaoExame),
      Math.abs(i.valorDB || 0).toFixed(2), formatDate(i.dataDB),
    ]);
    downloadCSV(headers, rows, 'nao_cobrados');
  };

  function downloadCSV(headers, rows, prefix) {
    const csv = '\uFEFF' + [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${prefix}_${resultado?.cabecalho?.numeroDemonstrativo || 'export'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const prioridadeStyle = (p) => {
    if (p === 'alta') return { ...styles.priorityBadge, backgroundColor: '#ffebee', color: '#c62828' };
    if (p === 'media') return { ...styles.priorityBadge, backgroundColor: '#fff3e0', color: '#e65100' };
    return { ...styles.priorityBadge, backgroundColor: '#f5f5f5', color: '#666' };
  };

  return (
    <div>
      {/* ── 1. Painel de Impacto Financeiro ── */}
      <div style={styles.summaryCards}>
        <div style={{ ...styles.summaryCard, borderLeftColor: '#c62828' }}>
          <div style={styles.summaryLabel}>Total de divergencias</div>
          <div style={{ ...styles.summaryValue, color: '#c62828' }}>{impactoFinanceiro.totalDivergencias}</div>
          <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
            {impactoFinanceiro.countDiv} valor + {resumo.naoEncontrado} N/E + {resumo.naoCobrado} N/C
          </div>
        </div>
        <div style={{ ...styles.summaryCard, borderLeftColor: '#c62828' }}>
          <div style={styles.summaryLabel}>UNIMED paga menos</div>
          <div style={{ ...styles.summaryValue, color: '#c62828', fontSize: '18px' }}>{formatCurrency(impactoFinanceiro.contraLab)}</div>
          <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>Laboratorio perde</div>
        </div>
        <div style={{ ...styles.summaryCard, borderLeftColor: '#2e7d32' }}>
          <div style={styles.summaryLabel}>UNIMED paga mais</div>
          <div style={{ ...styles.summaryValue, color: '#2e7d32', fontSize: '18px' }}>{formatCurrency(impactoFinanceiro.favorLab)}</div>
          <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>A favor do lab</div>
        </div>
        <div style={{ ...styles.summaryCard, borderLeftColor: '#e65100' }}>
          <div style={styles.summaryLabel}>Exposicao liquida</div>
          <div style={{ ...styles.summaryValue, color: impactoFinanceiro.exposicaoLiquida > 0 ? '#c62828' : '#2e7d32', fontSize: '18px' }}>
            {formatCurrency(impactoFinanceiro.exposicaoLiquida)}
          </div>
          <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>Contra + N/C - Favor</div>
        </div>
      </div>

      <div style={{ ...styles.infoCard, backgroundColor: '#e3f2fd', border: '1px solid #bbdefb', marginBottom: '24px' }}>
        <strong>Valor N/Encontrado (risco):</strong> {formatCurrency(impactoFinanceiro.valorNE)} |{' '}
        <strong>Valor N/Cobrado (receita potencial):</strong> {formatCurrency(impactoFinanceiro.valorNC)}
      </div>

      {/* ── 2. Divergências de Valor ── */}
      <div style={styles.section}>
        <h3
          style={{ ...styles.sectionTitle, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', userSelect: 'none' }}
          onClick={() => setSecaoCollapsed(p => ({ ...p, divValor: !p.divValor }))}
        >
          <span style={{ fontSize: '12px', color: '#999', fontFamily: 'monospace', width: '16px' }}>{secaoCollapsed.divValor ? '\u25B6' : '\u25BC'}</span>
          <span style={{ flex: 1 }}>Divergencias de Valor ({analiseValores.procedimentos.length})</span>
          <span style={{ fontSize: '12px', fontWeight: '400', color: impactoFinanceiro.contraLab > impactoFinanceiro.favorLab ? '#c62828' : '#2e7d32' }}>
            {formatCurrency(analiseValores.procedimentos.reduce((s, p) => s + p.totalDiff, 0))}
          </span>
        </h3>

        {!secaoCollapsed.divValor && (<>
        {analiseValores.sistematicos > 0 && (
          <div style={{ ...styles.infoCard, backgroundColor: '#fff8e1', border: '1px solid #ffe082', marginBottom: '16px' }}>
            <strong>Divergencias sistematicas detectadas:</strong> {analiseValores.sistematicos} procedimento(s) com diferenca fixa em todos os itens.
            Isso sugere <strong>tabela de precos desatualizada</strong> no sistema. Considere atualizar os valores cadastrados.
          </div>
        )}

        {/* Gráfico de barras horizontal - Top 10 */}
        {chartDataValores.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ fontSize: '13px', color: '#555', margin: '0 0 12px', fontWeight: '600' }}>Top 10 procedimentos por impacto total (R$)</h4>
            <ResponsiveContainer width="100%" height={Math.max(250, chartDataValores.length * 35)}>
              <BarChart data={chartDataValores} layout="vertical" margin={{ top: 5, right: 30, left: 100, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#666' }} tickFormatter={(v) => `R$ ${v.toFixed(0)}`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#666' }} width={90} />
                <Tooltip
                  formatter={(val) => [formatCurrency(val), 'Diferenca total']}
                  labelFormatter={(label) => `Procedimento: ${label}`}
                  contentStyle={{ fontSize: '12px', borderRadius: '8px' }}
                />
                <Bar dataKey="valor" radius={[0, 4, 4, 0]}>
                  {chartDataValores.map((entry, i) => (
                    <Cell key={i} fill={entry.valor >= 0 ? '#4caf50' : '#f44336'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Filtros */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Direcao</label>
            <select style={{ ...styles.select, width: '160px' }} value={divFiltroDir} onChange={e => setDivFiltroDir(e.target.value)}>
              <option value="todos">Todos</option>
              <option value="menos">UNIMED paga menos</option>
              <option value="mais">UNIMED paga mais</option>
            </select>
          </div>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Faixa de valor</label>
            <select style={{ ...styles.select, width: '160px' }} value={divFiltroFaixa} onChange={e => setDivFiltroFaixa(e.target.value)}>
              <option value="todos">Todas</option>
              <option value="arredondamento">&lt; R$ 1 (arredondamento)</option>
              <option value="pequena">R$ 1 a 5 (pequena)</option>
              <option value="media">R$ 5 a 20 (media)</option>
              <option value="grande">&gt; R$ 20 (grande)</option>
            </select>
          </div>
        </div>

        {/* Tabela de procedimentos */}
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.mapTable}>
            <thead>
              <tr>
                <th style={styles.mapTh}>Codigo</th>
                <th style={styles.mapTh}>Descricao</th>
                <th style={{ ...styles.mapTh, textAlign: 'center' }}>Qtd</th>
                <th style={{ ...styles.mapTh, textAlign: 'right' }}>Dif. media</th>
                <th style={{ ...styles.mapTh, textAlign: 'right' }}>Dif. total</th>
                <th style={{ ...styles.mapTh, textAlign: 'center' }}>Tipo</th>
              </tr>
            </thead>
            <tbody>
              {procFiltrados.length === 0 && (
                <tr><td colSpan={6} style={{ ...styles.mapTd, textAlign: 'center', color: '#999' }}>Nenhum procedimento com os filtros selecionados</td></tr>
              )}
              {procFiltrados.map((p, i) => (
                <tr key={i} style={{ backgroundColor: i % 2 ? '#fafafa' : '#fff' }}>
                  <td style={{ ...styles.mapTd, fontFamily: 'monospace', fontSize: '12px', wordBreak: 'break-word', maxWidth: '150px' }}>{formatCode(p.code)}</td>
                  <td style={{ ...styles.mapTd, fontSize: '12px', maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.desc}>{p.desc}</td>
                  <td style={{ ...styles.mapTd, textAlign: 'center' }}>{p.count}</td>
                  <td style={{ ...styles.mapTd, textAlign: 'right', color: p.avgDiff >= 0 ? '#2e7d32' : '#c62828', fontWeight: '600' }}>{formatCurrency(p.avgDiff)}</td>
                  <td style={{ ...styles.mapTd, textAlign: 'right', color: p.totalDiff >= 0 ? '#2e7d32' : '#c62828', fontWeight: '600' }}>{formatCurrency(p.totalDiff)}</td>
                  <td style={{ ...styles.mapTd, textAlign: 'center' }}>
                    {p.isConsistent ? (
                      <span style={{ ...styles.badge, backgroundColor: '#fff3e0', color: '#e65100', border: '1px solid #ffcc80' }}>Sistematica</span>
                    ) : (
                      <span style={{ ...styles.badge, backgroundColor: '#f5f5f5', color: '#888' }}>Variavel</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>)}
      </div>

      {/* ── 3. Não Encontrados ── */}
      <div style={styles.section}>
        <h3
          style={{ ...styles.sectionTitle, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', userSelect: 'none' }}
          onClick={() => setSecaoCollapsed(p => ({ ...p, ne: !p.ne }))}
        >
          <span style={{ fontSize: '12px', color: '#999', fontFamily: 'monospace', width: '16px' }}>{secaoCollapsed.ne ? '\u25B6' : '\u25BC'}</span>
          <span style={{ flex: 1 }}>Nao Encontrados ({analiseNE.total})</span>
          <span style={{ fontSize: '12px', fontWeight: '400', color: '#c62828' }}>{formatCurrency(analiseNE.valorTotal)}</span>
        </h3>
        {!secaoCollapsed.ne && (<>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '15px', fontWeight: '600', color: '#333' }}>{analiseNE.total} itens</span>
          <span style={{ fontSize: '14px', color: '#c62828', fontWeight: '600' }}>Valor em risco: {formatCurrency(analiseNE.valorTotal)}</span>
        </div>

        {/* Cards diagnóstico */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px', marginBottom: '16px' }}>
          <div style={{ ...styles.infoCard, backgroundColor: '#fff3e0', border: '1px solid #ffcc80' }}>
            <strong>Combo orfaos</strong><br />
            {analiseNE.comboOrfaos > 0 ? (
              <>{analiseNE.comboOrfaos} itens com codigos de combo ({formatCurrency(analiseNE.comboOrfaosValor)}). Possiveis componentes de exames combo que nao foram agrupados.</>
            ) : (
              <span style={{ color: '#888' }}>Nenhum combo orfao detectado</span>
            )}
          </div>
          <div style={{ ...styles.infoCard, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
            <strong>Identificacao CSV</strong><br />
            Com nome: <strong>{analiseNE.comNome}</strong> | Sem nome: <strong>{analiseNE.semNome}</strong>
            {analiseNE.semNome > 0 && analiseNE.comNome === 0 && (
              <div style={{ fontSize: '11px', color: '#e65100', marginTop: '4px' }}>Envie o CSV analitico para melhorar a identificacao</div>
            )}
          </div>
          <div style={{ ...styles.infoCard, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
            <strong>Top procedimento</strong><br />
            {analiseNE.procedimentos[0] ? (
              <>{analiseNE.procedimentos[0].code} - {analiseNE.procedimentos[0].count}x ({formatCurrency(analiseNE.procedimentos[0].valor)})</>
            ) : '-'}
          </div>
        </div>

        {/* Toggle agrupamento */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
          <button style={{ ...styles.clearBtn, ...(neAgrupar === 'guia' ? { backgroundColor: '#e3f2fd', borderColor: '#90caf9', color: '#1565c0' } : {}) }} onClick={() => setNeAgrupar('guia')}>Por guia</button>
          <button style={{ ...styles.clearBtn, ...(neAgrupar === 'procedimento' ? { backgroundColor: '#e3f2fd', borderColor: '#90caf9', color: '#1565c0' } : {}) }} onClick={() => setNeAgrupar('procedimento')}>Por procedimento</button>
        </div>

        {neAgrupar === 'guia' ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.mapTable}>
              <thead>
                <tr>
                  <th style={styles.mapTh}>Guia</th>
                  <th style={styles.mapTh}>Paciente</th>
                  <th style={{ ...styles.mapTh, textAlign: 'center' }}>Itens</th>
                  <th style={{ ...styles.mapTh, textAlign: 'right' }}>Valor</th>
                  <th style={styles.mapTh}>Data</th>
                </tr>
              </thead>
              <tbody>
                {analiseNE.guias.length === 0 && (
                  <tr><td colSpan={5} style={{ ...styles.mapTd, textAlign: 'center', color: '#999' }}>Nenhum item nao encontrado</td></tr>
                )}
                {analiseNE.guias.map((g, i) => {
                  const key = `ne:${g.guia}`;
                  const isCollapsed = neCollapsed[key];
                  return (
                    <React.Fragment key={i}>
                      <tr
                        style={{ cursor: 'pointer', backgroundColor: i % 2 ? '#fafafa' : '#fff' }}
                        onClick={() => setNeCollapsed(prev => ({ ...prev, [key]: !prev[key] }))}
                      >
                        <td style={styles.mapTd}>
                          <span style={{ fontFamily: 'monospace', marginRight: '8px', fontSize: '11px', color: '#999' }}>{isCollapsed ? '\u25B6' : '\u25BC'}</span>
                          {g.guia}
                        </td>
                        <td style={{ ...styles.mapTd, fontWeight: '500' }}>{g.paciente}</td>
                        <td style={{ ...styles.mapTd, textAlign: 'center' }}>{g.itens.length}</td>
                        <td style={{ ...styles.mapTd, textAlign: 'right', fontWeight: '600', color: '#c62828' }}>{formatCurrency(g.valor)}</td>
                        <td style={styles.mapTd}></td>
                      </tr>
                      {!isCollapsed && g.itens.map((item, j) => (
                        <tr key={j} style={{ backgroundColor: '#f8f8f8' }}>
                          <td style={{ ...styles.mapTd, paddingLeft: '32px', fontFamily: 'monospace', fontSize: '12px', wordBreak: 'break-word' }}>{formatCode(item.codigoProcedimento)}</td>
                          <td style={{ ...styles.mapTd, fontSize: '12px' }} colSpan={2}>{item.descricaoProcedimento}</td>
                          <td style={{ ...styles.mapTd, textAlign: 'right' }}>{formatCurrency(item.valorXML)}</td>
                          <td style={{ ...styles.mapTd, fontSize: '12px' }}>{formatDate(item.dataRealizacao)}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.mapTable}>
              <thead>
                <tr>
                  <th style={styles.mapTh}>Codigo</th>
                  <th style={styles.mapTh}>Descricao</th>
                  <th style={{ ...styles.mapTh, textAlign: 'center' }}>Qtd</th>
                  <th style={{ ...styles.mapTh, textAlign: 'right' }}>Valor total</th>
                </tr>
              </thead>
              <tbody>
                {analiseNE.procedimentos.map((p, i) => (
                  <tr key={i} style={{ backgroundColor: i % 2 ? '#fafafa' : '#fff' }}>
                    <td style={{ ...styles.mapTd, fontFamily: 'monospace', fontSize: '12px', wordBreak: 'break-word', maxWidth: '150px' }}>{formatCode(p.code)}</td>
                    <td style={{ ...styles.mapTd, fontSize: '12px' }}>{(p.desc || '').substring(0, 80)}</td>
                    <td style={{ ...styles.mapTd, textAlign: 'center' }}>{p.count}</td>
                    <td style={{ ...styles.mapTd, textAlign: 'right', fontWeight: '600', color: '#c62828' }}>{formatCurrency(p.valor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Card de ações sugeridas */}
        <div style={{ ...styles.infoCard, backgroundColor: '#ffebee', border: '1px solid #ef9a9a', marginTop: '16px' }}>
          <strong>Acoes sugeridas para investigacao:</strong>
          <ol style={{ margin: '8px 0 0', paddingLeft: '20px', lineHeight: '2' }}>
            <li>Verificar se os pacientes foram atendidos com <strong>outro convenio</strong> (ex: particular) no periodo</li>
            <li>Comparar as <strong>datas do XML</strong> com as datas de atendimento no sistema (diferenca &gt; 5 dias?)</li>
            <li>Checar se houve <strong>cancelamento</strong> ou <strong>reagendamento</strong> de atendimentos</li>
            <li>Validar se os <strong>codigos TUSS</strong> do XML correspondem aos cadastrados no sistema</li>
          </ol>
        </div>
        </>)}
      </div>

      {/* ── 4. Não Cobrados ── */}
      <div style={styles.section}>
        <h3
          style={{ ...styles.sectionTitle, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', userSelect: 'none' }}
          onClick={() => setSecaoCollapsed(p => ({ ...p, nc: !p.nc }))}
        >
          <span style={{ fontSize: '12px', color: '#999', fontFamily: 'monospace', width: '16px' }}>{secaoCollapsed.nc ? '\u25B6' : '\u25BC'}</span>
          <span style={{ flex: 1 }}>Nao Cobrados ({analiseNC.total})</span>
          <span style={{ fontSize: '12px', fontWeight: '400', color: '#1565c0' }}>{formatCurrency(analiseNC.valorTotal)}</span>
        </h3>
        {!secaoCollapsed.nc && (<>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '15px', fontWeight: '600', color: '#333' }}>{analiseNC.total} itens</span>
          <span style={{ fontSize: '14px', color: '#1565c0', fontWeight: '600' }}>Valor potencial: {formatCurrency(analiseNC.valorTotal)}</span>
        </div>

        {/* Pizza de situação + Callouts */}
        <div style={{ display: 'grid', gridTemplateColumns: analiseNC.pieData.length > 0 ? '300px 1fr' : '1fr', gap: '20px', marginBottom: '20px' }}>
          {analiseNC.pieData.length > 0 && (
            <div>
              <h4 style={{ fontSize: '13px', color: '#555', margin: '0 0 8px', fontWeight: '600' }}>Distribuicao por situacao do exame</h4>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={analiseNC.pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={2} dataKey="value">
                    {analiseNC.pieData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(val, name, props) => [`${val} itens (${formatCurrency(props.payload.valor)})`, name]}
                    contentStyle={{ fontSize: '12px', borderRadius: '8px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px' }}>
                {analiseNC.pieData.map((d, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ width: '10px', height: '10px', borderRadius: '2px', backgroundColor: d.color, flexShrink: 0 }}></span>
                    <span style={{ color: '#555' }}>{d.name}</span>
                    <span style={{ marginLeft: 'auto', fontWeight: '600', color: '#333' }}>{d.value} ({formatCurrency(d.valor)})</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {analiseNC.entregues > 0 && (
              <div
                style={{ ...styles.infoCard, backgroundColor: ncFiltro === 'entregues' ? '#ffcdd2' : '#ffebee', border: '1px solid #ef9a9a', cursor: 'pointer', transition: 'all 0.2s' }}
                onClick={() => setNcFiltro(ncFiltro === 'entregues' ? 'todos' : 'entregues')}
              >
                <strong>Possivel glosa silenciosa:</strong> {analiseNC.entregues} exame(s) com situacao Impresso/Entregue ({formatCurrency(analiseNC.entreguesValor)})
                mas nao incluidos no pagamento. <strong>Contatar UNIMED para verificar motivo.</strong>
                <div style={{ fontSize: '11px', color: '#c62828', marginTop: '6px' }}>{ncFiltro === 'entregues' ? '\u25BC Mostrando na tabela abaixo. Clique para remover filtro.' : '\u25B6 Clique para ver na tabela abaixo'}</div>
              </div>
            )}
            {analiseNC.semTuss > 0 && (
              <div
                style={{ ...styles.infoCard, backgroundColor: ncFiltro === 'semTuss' ? '#ffe0b2' : '#fff3e0', border: '1px solid #ffcc80', cursor: 'pointer', transition: 'all 0.2s' }}
                onClick={() => setNcFiltro(ncFiltro === 'semTuss' ? 'todos' : 'semTuss')}
              >
                <strong>Sem codigo TUSS:</strong> {analiseNC.semTuss} exame(s) ({formatCurrency(analiseNC.semTussValor)})
                sem codigo TUSS cadastrado. <strong>Cadastrar codigo na tabela de precos</strong> para que sejam incluidos na conferencia.
                <div style={{ fontSize: '11px', color: '#e65100', marginTop: '6px' }}>{ncFiltro === 'semTuss' ? '\u25BC Mostrando na tabela abaixo. Clique para remover filtro.' : '\u25B6 Clique para ver na tabela abaixo'}</div>
              </div>
            )}
            {analiseNC.pendentes > 0 && (
              <div
                style={{ ...styles.infoCard, backgroundColor: ncFiltro === 'pendentes' ? '#e0e0e0' : '#f5f5f5', border: '1px solid #e0e0e0', cursor: 'pointer', transition: 'all 0.2s' }}
                onClick={() => setNcFiltro(ncFiltro === 'pendentes' ? 'todos' : 'pendentes')}
              >
                <strong>Exames pendentes:</strong> {analiseNC.pendentes} exame(s) ({formatCurrency(analiseNC.pendentesValor)})
                com situacao "Pendente". <strong>Aguardar liberacao</strong> antes de contestar.
                <div style={{ fontSize: '11px', color: '#666', marginTop: '6px' }}>{ncFiltro === 'pendentes' ? '\u25BC Mostrando na tabela abaixo. Clique para remover filtro.' : '\u25B6 Clique para ver na tabela abaixo'}</div>
              </div>
            )}
          </div>
        </div>

        {/* Filtro ativo */}
        {ncFiltro !== 'todos' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
            <span style={{ fontSize: '13px', color: '#555' }}>
              Filtro ativo: <strong>{ncFiltro === 'entregues' ? 'Glosa silenciosa (I/E)' : ncFiltro === 'semTuss' ? 'Sem codigo TUSS' : 'Pendentes'}</strong>
              {' '}({ncPacientesFiltrados.reduce((s, p) => s + p.itens.length, 0)} itens)
            </span>
            <button style={{ ...styles.clearBtn, padding: '4px 12px', fontSize: '12px' }} onClick={() => setNcFiltro('todos')}>Limpar filtro</button>
          </div>
        )}

        {/* Tabela agrupada por paciente */}
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.mapTable}>
            <thead>
              <tr>
                <th style={styles.mapTh}>Paciente</th>
                <th style={styles.mapTh}>Protocolo</th>
                <th style={{ ...styles.mapTh, textAlign: 'center' }}>Itens</th>
                <th style={{ ...styles.mapTh, textAlign: 'right' }}>Valor</th>
                <th style={styles.mapTh}>Data</th>
              </tr>
            </thead>
            <tbody>
              {ncPacientesFiltrados.length === 0 && (
                <tr><td colSpan={5} style={{ ...styles.mapTd, textAlign: 'center', color: '#999' }}>Nenhum item nao cobrado{ncFiltro !== 'todos' ? ' com este filtro' : ''}</td></tr>
              )}
              {ncPacientesFiltrados.map((pac, i) => {
                const key = `nc:${pac.paciente}`;
                const isCollapsed = ncCollapsed[key];
                return (
                  <React.Fragment key={i}>
                    <tr
                      style={{ cursor: 'pointer', backgroundColor: i % 2 ? '#fafafa' : '#fff' }}
                      onClick={() => setNcCollapsed(prev => ({ ...prev, [key]: !prev[key] }))}
                    >
                      <td style={{ ...styles.mapTd, fontWeight: '500' }}>
                        <span style={{ fontFamily: 'monospace', marginRight: '8px', fontSize: '11px', color: '#999' }}>{isCollapsed ? '\u25B6' : '\u25BC'}</span>
                        {pac.paciente}
                      </td>
                      <td style={styles.mapTd}>{pac.protocolo}</td>
                      <td style={{ ...styles.mapTd, textAlign: 'center' }}>{pac.itens.length}</td>
                      <td style={{ ...styles.mapTd, textAlign: 'right', fontWeight: '600', color: '#1565c0' }}>{formatCurrency(pac.valor)}</td>
                      <td style={styles.mapTd}></td>
                    </tr>
                    {!isCollapsed && pac.itens.map((item, j) => {
                      const sitStyle = situacaoExameStyle(item.situacaoExame);
                      return (
                        <tr key={j} style={{ backgroundColor: '#f8f8f8' }}>
                          <td style={{ ...styles.mapTd, paddingLeft: '32px', fontSize: '12px' }}>
                            <span style={{ fontFamily: 'monospace', marginRight: '6px', wordBreak: 'break-word' }}>{formatCode(item.codigoProcedimento)}</span>
                            {item.descricaoProcedimento || item.exameNome}
                          </td>
                          <td style={{ ...styles.mapTd, fontSize: '12px' }}>{item.protocolo}</td>
                          <td style={{ ...styles.mapTd, textAlign: 'center' }}>
                            <span style={{ ...styles.badge, backgroundColor: sitStyle.bg, color: sitStyle.color, fontSize: '10px' }}>{situacaoExameLabel(item.situacaoExame)}</span>
                          </td>
                          <td style={{ ...styles.mapTd, textAlign: 'right' }}>{formatCurrency(Math.abs(item.valorDB))}</td>
                          <td style={{ ...styles.mapTd, fontSize: '12px' }}>{formatDate(item.dataDB)}</td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        </>)}
      </div>

      {/* ── 5. Resumo de Ações ── */}
      <div style={styles.section}>
        <h3
          style={{ ...styles.sectionTitle, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', userSelect: 'none' }}
          onClick={() => setSecaoCollapsed(p => ({ ...p, acoes: !p.acoes }))}
        >
          <span style={{ fontSize: '12px', color: '#999', fontFamily: 'monospace', width: '16px' }}>{secaoCollapsed.acoes ? '\u25B6' : '\u25BC'}</span>
          <span style={{ flex: 1 }}>Resumo de Acoes ({resumoAcoes.length})</span>
        </h3>

        {!secaoCollapsed.acoes && (<>
        {resumoAcoes.length === 0 ? (
          <div style={{ ...styles.infoCard, backgroundColor: '#e8f5e9', border: '1px solid #c8e6c9' }}>
            Nenhuma acao prioritaria identificada. Todos os itens parecem estar em ordem.
          </div>
        ) : (
          <div>
            {resumoAcoes.map((acao, i) => (
              <div key={i} style={styles.checklistItem}>
                <div style={styles.checklistNumber}>{i + 1}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                    <strong style={{ fontSize: '14px', color: '#333' }}>{acao.titulo}</strong>
                    <span style={prioridadeStyle(acao.prioridade)}>{acao.prioridade}</span>
                  </div>
                  <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.5' }}>{acao.descricao}</div>
                </div>
                <div style={{ textAlign: 'right', minWidth: '100px', flexShrink: 0 }}>
                  <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Impacto</div>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: '#c62828' }}>{formatCurrency(acao.impacto)}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Botões de export */}
        <div style={{ display: 'flex', gap: '10px', marginTop: '20px', flexWrap: 'wrap' }}>
          <button style={styles.exportBtn} onClick={exportDivergenciasCSV}>Exportar Divergencias (CSV)</button>
          <button style={styles.exportBtn} onClick={exportNECSV}>Exportar N/Encontrados (CSV)</button>
          <button style={styles.exportBtn} onClick={exportNCCSV}>Exportar N/Cobrados (CSV)</button>
        </div>
        </>)}
      </div>
    </div>
  );
}

// ── Componente Principal ─────────────────────────────────────────────────

function ConferenciaConvenios() {
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fileName, setFileName] = useState('');
  const [activeTab, setActiveTab] = useState('conferencia');

  // Filtros
  const [filtroStatus, setFiltroStatus] = useState('TODOS');
  const [filtroBusca, setFiltroBusca] = useState('');
  const [sortCol, setSortCol] = useState('status');
  const [sortDir, setSortDir] = useState('asc');

  const [collapsed, setCollapsed] = useState({});

  const fileRef = useRef(null);
  const csvFileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvFileName, setCsvFileName] = useState('');

  const handleUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.xml')) {
      setError('Apenas arquivos XML TISS são aceitos');
      return;
    }

    setLoading(true);
    setError('');
    setResultado(null);
    setFileName(file.name);

    try {
      const formData = new FormData();
      formData.append('arquivo', file);
      if (csvFile) {
        formData.append('csv', csvFile);
      }

      const res = await axios.post(`${API_URL}/financeiro/conferencia-convenios/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });

      setResultado(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Erro ao processar arquivo');
    } finally {
      setLoading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleUpload(file);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
    e.target.value = '';
  };

  const itensFiltrados = useMemo(() => {
    if (!resultado?.itens) return [];
    let arr = resultado.itens;

    if (filtroStatus !== 'TODOS') {
      arr = arr.filter(i => i.status === filtroStatus);
    }
    if (filtroBusca.trim()) {
      const q = filtroBusca.toLowerCase();
      arr = arr.filter(i =>
        (i.numeroGuia || '').toLowerCase().includes(q) ||
        (i.codigoProcedimento || '').toLowerCase().includes(q) ||
        (i.descricaoProcedimento || '').toLowerCase().includes(q) ||
        (i.paciente || '').toLowerCase().includes(q) ||
        (i.protocolo || '').toLowerCase().includes(q)
      );
    }

    arr = [...arr].sort((a, b) => {
      let va = a[sortCol], vb = b[sortCol];
      if (typeof va === 'number' && typeof vb === 'number') {
        return sortDir === 'asc' ? va - vb : vb - va;
      }
      va = String(va || '');
      vb = String(vb || '');
      return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });

    return arr;
  }, [resultado, filtroStatus, filtroBusca, sortCol, sortDir]);

  // Agrupar por Status → Paciente
  const STATUS_ORDER = ['OK', 'DIVERGENCIA_VALOR', 'NAO_ENCONTRADO', 'NAO_COBRADO', 'FORA_PERIODO'];

  const grupos = useMemo(() => {
    const byStatus = {};
    for (const item of itensFiltrados) {
      if (!byStatus[item.status]) byStatus[item.status] = {};
      const pac = item.paciente || '(Sem paciente)';
      if (!byStatus[item.status][pac]) byStatus[item.status][pac] = [];
      byStatus[item.status][pac].push(item);
    }
    return STATUS_ORDER
      .filter(s => byStatus[s])
      .map(status => {
        const pacientes = byStatus[status];
        const pacList = Object.keys(pacientes).sort().map(pac => {
          const itens = pacientes[pac];
          return {
            paciente: pac,
            itens,
            totalXML: itens.reduce((s, i) => s + (i.valorXML || 0), 0),
            totalDB: itens.reduce((s, i) => s + (i.valorDB || 0), 0),
          };
        });
        const allItens = pacList.flatMap(p => p.itens);
        return {
          status,
          pacientes: pacList,
          count: allItens.length,
          totalXML: allItens.reduce((s, i) => s + (i.valorXML || 0), 0),
          totalDB: allItens.reduce((s, i) => s + (i.valorDB || 0), 0),
        };
      });
  }, [itensFiltrados]);

  const toggleCollapse = (key) => {
    setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const collapseAllStatus = () => {
    const all = {};
    for (const g of grupos) all[`s:${g.status}`] = true;
    setCollapsed(all);
  };

  const collapseAllPacientes = () => {
    const all = { ...collapsed };
    for (const g of grupos) {
      delete all[`s:${g.status}`];
      for (const pac of g.pacientes) all[`p:${g.status}:${pac.paciente}`] = true;
    }
    setCollapsed(all);
  };

  const expandAll = () => setCollapsed({});

  // Totais em R$ por status
  const totaisPorStatus = useMemo(() => {
    if (!resultado?.itens) return {};
    const t = { OK: 0, DIVERGENCIA_VALOR: 0, NAO_ENCONTRADO: 0, NAO_COBRADO: 0, FORA_PERIODO: 0 };
    for (const item of resultado.itens) {
      if (item.status === 'OK' || item.status === 'DIVERGENCIA_VALOR') t[item.status] += (item.valorXML || 0);
      else if (item.status === 'NAO_ENCONTRADO') t[item.status] += (item.valorXML || 0);
      else if (item.status === 'NAO_COBRADO' || item.status === 'FORA_PERIODO') t[item.status] += Math.abs(item.valorDB || 0);
    }
    return t;
  }, [resultado]);

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const exportCSV = () => {
    if (!itensFiltrados.length) return;
    const headers = ['Status', 'Guia', 'Procedimento', 'Descricao', 'Paciente', 'Protocolo', 'Valor XML', 'Valor Sistema', 'Diferenca', 'Data'];
    const rows = itensFiltrados.map(i => [
      STATUS_CONFIG[i.status]?.label || i.status,
      i.numeroGuia,
      i.codigoProcedimento,
      `"${(i.descricaoProcedimento || '').replace(/"/g, '""')}"`,
      `"${(i.paciente || '').replace(/"/g, '""')}"`,
      i.protocolo,
      (i.valorXML || 0).toFixed(2),
      (i.valorDB || 0).toFixed(2),
      (i.diferenca || 0).toFixed(2),
      formatDate(i.dataRealizacao),
    ]);

    const csv = '\uFEFF' + [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `conferencia_${resultado?.cabecalho?.numeroDemonstrativo || 'export'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleNovaConferencia = () => {
    setResultado(null);
    setError('');
    setFileName('');
    setCsvFile(null);
    setCsvFileName('');
    setFiltroStatus('TODOS');
    setFiltroBusca('');
    setActiveTab('conferencia');
  };

  const sortArrow = (col) => sortCol === col ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : '';

  return (
    <div style={styles.page} className="data-page">
      <div style={{ padding: '24px 32px' }}>
        <div style={styles.breadcrumb}>
          <span style={styles.breadcrumbLink} onClick={() => window.history.back()}>Financeiro</span>
          <span> / Conferencia de Convenios</span>
        </div>

        <div style={styles.pageHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={styles.pageIcon}>🔍</span>
            <div>
              <h1 style={styles.pageTitle}>Conferencia de Convenios</h1>
              <p style={styles.pageSubtitle}>Upload do XML TISS para conferir guias e valores contra o sistema</p>
            </div>
          </div>
          {resultado && (
            <button style={{ ...styles.clearBtn, padding: '10px 20px' }} onClick={handleNovaConferencia}>
              Nova Conferencia
            </button>
          )}
        </div>

        {/* Upload area */}
        {!resultado && !loading && (
          <div style={styles.uploadCard}>
            <div
              style={{ ...styles.dropZone, ...(dragOver ? styles.dropZoneActive : {}) }}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileRef.current?.click()}
            >
              <div style={styles.dropIcon}>{'\uD83D\uDCC4'}</div>
              <p style={styles.dropText}>Arraste o arquivo XML TISS aqui ou clique para selecionar</p>
              <p style={styles.dropHint}>Formato aceito: XML (Demonstrativo de Analise de Conta - TISS)</p>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".xml"
              style={styles.fileInput}
              onChange={handleFileChange}
            />

            {/* CSV opcional */}
            <div style={{ marginTop: '16px', padding: '16px', backgroundColor: '#fafafa', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'center' }}>
                <span style={{ fontSize: '13px', color: '#666' }}>CSV analitico (opcional):</span>
                {csvFileName ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '13px', color: '#2e7d32', fontWeight: '500' }}>{csvFileName}</span>
                    <button
                      style={{ ...styles.clearBtn, padding: '4px 10px', fontSize: '12px' }}
                      onClick={(e) => { e.stopPropagation(); setCsvFile(null); setCsvFileName(''); }}
                    >
                      Remover
                    </button>
                  </div>
                ) : (
                  <button
                    style={{ ...styles.clearBtn, padding: '6px 14px', fontSize: '13px' }}
                    onClick={(e) => { e.stopPropagation(); csvFileRef.current?.click(); }}
                  >
                    Selecionar CSV
                  </button>
                )}
              </div>
              <p style={{ fontSize: '11px', color: '#999', margin: '8px 0 0', textAlign: 'center' }}>
                O CSV analitico da UNIMED permite identificar o nome do beneficiario por guia, melhorando o match dos itens nao encontrados.
              </p>
            </div>
            <input
              ref={csvFileRef}
              type="file"
              accept=".csv"
              style={styles.fileInput}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) { setCsvFile(f); setCsvFileName(f.name); }
                e.target.value = '';
              }}
            />

            {error && (
              <p style={{ color: '#c62828', marginTop: '16px', fontSize: '14px' }}>{error}</p>
            )}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div style={styles.loading}>
            <p style={{ fontSize: '16px' }}>Processando {fileName}...</p>
            <p style={{ fontSize: '13px', color: '#999' }}>Analisando guias e cruzando com o sistema</p>
          </div>
        )}

        {/* Error after upload */}
        {error && resultado === null && !loading && fileName && (
          <div style={{ ...styles.uploadCard, borderLeft: '4px solid #c62828' }}>
            <p style={{ color: '#c62828', margin: 0 }}>{error}</p>
          </div>
        )}

        {/* Resultados */}
        {resultado && (
          <>
            {/* Cabecalho do demonstrativo */}
            <div style={styles.cabecalhoCard}>
              <div style={styles.cabInfo}>
                <span style={styles.cabLabel}>Demonstrativo</span>
                <span style={styles.cabValue}>#{resultado.cabecalho.numeroDemonstrativo}</span>
              </div>
              <div style={styles.cabInfo}>
                <span style={styles.cabLabel}>Operadora</span>
                <span style={styles.cabValue}>{resultado.cabecalho.nomeOperadora}</span>
              </div>
              <div style={styles.cabInfo}>
                <span style={styles.cabLabel}>CNPJ</span>
                <span style={{ ...styles.cabValue, fontWeight: '400', fontSize: '13px' }}>{resultado.cabecalho.cnpj}</span>
              </div>
              <div style={styles.cabInfo}>
                <span style={styles.cabLabel}>Emissao</span>
                <span style={styles.cabValue}>{formatDate(resultado.cabecalho.dataEmissao)}</span>
              </div>
              <div style={styles.cabInfo}>
                <span style={styles.cabLabel}>Periodo</span>
                <span style={styles.cabValue}>{formatDate(resultado.resumo.periodoInicio)} a {formatDate(resultado.resumo.periodoFim)}</span>
              </div>
              <div style={styles.cabInfo}>
                <span style={styles.cabLabel}>Arquivo</span>
                <span style={{ ...styles.cabValue, fontWeight: '400', fontSize: '13px' }}>{fileName}</span>
              </div>
              {csvFileName && (
                <div style={styles.cabInfo}>
                  <span style={styles.cabLabel}>CSV Analitico</span>
                  <span style={{ ...styles.cabValue, fontWeight: '400', fontSize: '13px', color: '#2e7d32' }}>{csvFileName}</span>
                </div>
              )}
            </div>

            {/* Tabs */}
            <div style={styles.tabs}>
              <button
                style={{ ...styles.tab, ...(activeTab === 'conferencia' ? styles.tabActive : {}) }}
                onClick={() => setActiveTab('conferencia')}
              >
                Conferencia ({resultado.itens.length} itens)
              </button>
              <button
                style={{ ...styles.tab, ...(activeTab === 'relatorio' ? styles.tabActive : {}) }}
                onClick={() => setActiveTab('relatorio')}
              >
                Relatorio Tecnico
              </button>
              <button
                style={{ ...styles.tab, ...(activeTab === 'divergencias' ? styles.tabActive : {}) }}
                onClick={() => setActiveTab('divergencias')}
              >
                Divergencias ({resultado.resumo.divergenciaValor + resultado.resumo.naoEncontrado + resultado.resumo.naoCobrado})
              </button>
            </div>

            {activeTab === 'relatorio' ? (
              <RelatorioTecnico resultado={resultado} />
            ) : activeTab === 'divergencias' ? (
              <DivergenciasTab resultado={resultado} />
            ) : (
              <>
                {/* Summary cards */}
                <div style={styles.summaryCards}>
                  <div style={{ ...styles.summaryCard, borderLeftColor: '#666' }}>
                    <div style={styles.summaryLabel}>Itens XML / Sistema</div>
                    <div style={styles.summaryValue}>{resultado.resumo.totalItensXML} / {resultado.resumo.totalItensDB}</div>
                  </div>
                  <div style={{ ...styles.summaryCard, borderLeftColor: '#2e7d32' }}>
                    <div style={styles.summaryLabel}>Itens OK</div>
                    <div style={{ ...styles.summaryValue, color: '#2e7d32' }}>{resultado.resumo.ok}</div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>{formatCurrency(totaisPorStatus.OK)}</div>
                  </div>
                  <div style={{ ...styles.summaryCard, borderLeftColor: '#e65100' }}>
                    <div style={styles.summaryLabel}>Valor divergente</div>
                    <div style={{ ...styles.summaryValue, color: '#e65100' }}>{resultado.resumo.divergenciaValor}</div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>{formatCurrency(totaisPorStatus.DIVERGENCIA_VALOR)}</div>
                  </div>
                  <div style={{ ...styles.summaryCard, borderLeftColor: '#c62828' }}>
                    <div style={styles.summaryLabel}>N/encontrado no sistema</div>
                    <div style={{ ...styles.summaryValue, color: '#c62828' }}>{resultado.resumo.naoEncontrado}</div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>{formatCurrency(totaisPorStatus.NAO_ENCONTRADO)}</div>
                  </div>
                  <div style={{ ...styles.summaryCard, borderLeftColor: '#1565c0' }}>
                    <div style={styles.summaryLabel}>N/cobrado UNIMED</div>
                    <div style={{ ...styles.summaryValue, color: '#1565c0' }}>{resultado.resumo.naoCobrado}</div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>{formatCurrency(totaisPorStatus.NAO_COBRADO)}</div>
                  </div>
                  {resultado.resumo.foraPeriodo > 0 && (
                    <div style={{ ...styles.summaryCard, borderLeftColor: '#7b1fa2' }}>
                      <div style={styles.summaryLabel}>Fora do periodo</div>
                      <div style={{ ...styles.summaryValue, color: '#7b1fa2' }}>{resultado.resumo.foraPeriodo}</div>
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>{formatCurrency(totaisPorStatus.FORA_PERIODO)}</div>
                    </div>
                  )}
                </div>

                {/* Totais financeiros */}
                <div style={{ ...styles.summaryCards, marginBottom: '16px' }}>
                  <div style={{ ...styles.summaryCard, borderLeftColor: '#1976d2' }}>
                    <div style={styles.summaryLabel}>Total XML (UNIMED)</div>
                    <div style={{ ...styles.summaryValue, fontSize: '18px' }}>{formatCurrency(resultado.resumo.valorTotalXML)}</div>
                  </div>
                  <div style={{ ...styles.summaryCard, borderLeftColor: '#1976d2' }}>
                    <div style={styles.summaryLabel}>Total Sistema</div>
                    <div style={{ ...styles.summaryValue, fontSize: '18px' }}>{formatCurrency(resultado.resumo.valorTotalDB)}</div>
                  </div>
                  <div style={{ ...styles.summaryCard, borderLeftColor: resultado.resumo.diferencaTotal >= 0 ? '#2e7d32' : '#c62828' }}>
                    <div style={styles.summaryLabel}>Diferenca</div>
                    <div style={{ ...styles.summaryValue, fontSize: '18px', color: resultado.resumo.diferencaTotal >= 0 ? '#2e7d32' : '#c62828' }}>
                      {formatCurrency(resultado.resumo.diferencaTotal)}
                    </div>
                  </div>
                </div>

                {/* Filters row */}
                <div style={styles.filtersRow}>
                  <div style={styles.filterGroup}>
                    <label style={styles.filterLabel}>Status</label>
                    <select style={styles.select} value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}>
                      <option value="TODOS">Todos ({resultado.itens.length})</option>
                      <option value="OK">OK ({resultado.resumo.ok})</option>
                      <option value="DIVERGENCIA_VALOR">Valor divergente ({resultado.resumo.divergenciaValor})</option>
                      <option value="NAO_ENCONTRADO">N/encontrado ({resultado.resumo.naoEncontrado})</option>
                      <option value="NAO_COBRADO">N/cobrado UNIMED ({resultado.resumo.naoCobrado})</option>
                      <option value="FORA_PERIODO">Fora do periodo ({resultado.resumo.foraPeriodo || 0})</option>
                    </select>
                  </div>
                  <div style={{ ...styles.filterGroup, flex: 1, minWidth: '200px' }}>
                    <label style={styles.filterLabel}>Buscar</label>
                    <input
                      style={styles.input}
                      placeholder="Guia, procedimento, paciente, protocolo..."
                      value={filtroBusca}
                      onChange={e => setFiltroBusca(e.target.value)}
                    />
                  </div>
                  <button style={styles.exportBtn} onClick={exportCSV} disabled={!itensFiltrados.length}>
                    Exportar CSV
                  </button>
                </div>

                {/* Collapse/Expand controls */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                  <button style={styles.clearBtn} onClick={expandAll}>Expandir tudo</button>
                  <button style={styles.clearBtn} onClick={collapseAllPacientes}>Colapsar pacientes</button>
                  <button style={styles.clearBtn} onClick={collapseAllStatus}>Colapsar tudo</button>
                </div>

                {/* Table */}
                <div style={styles.tableCard}>
                  <div style={styles.tableWrap}>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th style={{ ...styles.th, width: '70px' }} onClick={() => handleSort('numeroGuia')}>Guia{sortArrow('numeroGuia')}</th>
                          <th style={{ ...styles.th, width: '80px' }} onClick={() => handleSort('codigoProcedimento')}>Cod.{sortArrow('codigoProcedimento')}</th>
                          <th style={{ ...styles.th, width: '200px' }} onClick={() => handleSort('descricaoProcedimento')}>Descricao{sortArrow('descricaoProcedimento')}</th>
                          <th style={{ ...styles.th, width: '80px' }} onClick={() => handleSort('protocolo')}>Protocolo{sortArrow('protocolo')}</th>
                          <th style={{ ...styles.th, width: '85px', textAlign: 'right' }} onClick={() => handleSort('valorXML')}>Val. XML{sortArrow('valorXML')}</th>
                          <th style={{ ...styles.th, width: '85px', textAlign: 'right' }} onClick={() => handleSort('valorDB')}>Val. Sist.{sortArrow('valorDB')}</th>
                          <th style={{ ...styles.th, width: '85px', textAlign: 'right' }} onClick={() => handleSort('diferenca')}>Diferenca{sortArrow('diferenca')}</th>
                          <th style={{ ...styles.th, width: '82px' }} onClick={() => handleSort('dataRealizacao')}>Data{sortArrow('dataRealizacao')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {grupos.length === 0 && (
                          <tr>
                            <td colSpan={8} style={styles.empty}>Nenhum item encontrado</td>
                          </tr>
                        )}
                        {grupos.map(grupo => {
                          const cfg = STATUS_CONFIG[grupo.status] || { label: grupo.status, bg: '#eee', color: '#333' };
                          const statusKey = `s:${grupo.status}`;
                          const statusCollapsed = collapsed[statusKey];
                          return (
                            <React.Fragment key={grupo.status}>
                              {/* Status header row */}
                              <tr
                                onClick={() => toggleCollapse(statusKey)}
                                style={{ cursor: 'pointer', backgroundColor: cfg.bg }}
                              >
                                <td colSpan={8} style={{ padding: '10px 12px', borderBottom: '2px solid ' + cfg.color + '40' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ fontSize: '12px', color: '#666', fontFamily: 'monospace', width: '16px' }}>{statusCollapsed ? '\u25B6' : '\u25BC'}</span>
                                    <span style={{ ...styles.badge, backgroundColor: cfg.bg, color: cfg.color, border: '1px solid ' + cfg.color + '40' }}>{cfg.label}</span>
                                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#333' }}>{grupo.count} itens</span>
                                    <span style={{ fontSize: '12px', color: '#666', marginLeft: 'auto' }}>
                                      XML: {formatCurrency(grupo.totalXML)} | Sist: {formatCurrency(grupo.totalDB)} | Dif: <span style={{ color: (grupo.totalXML - grupo.totalDB) >= -0.01 ? '#2e7d32' : '#c62828', fontWeight: '600' }}>{formatCurrency(Math.round((grupo.totalXML - grupo.totalDB) * 100) / 100)}</span>
                                    </span>
                                  </div>
                                </td>
                              </tr>
                              {!statusCollapsed && grupo.pacientes.map(pac => {
                                const pacKey = `p:${grupo.status}:${pac.paciente}`;
                                const pacCollapsed = collapsed[pacKey];
                                return (
                                  <React.Fragment key={pacKey}>
                                    {/* Paciente header row */}
                                    <tr
                                      onClick={() => toggleCollapse(pacKey)}
                                      style={{ cursor: 'pointer', backgroundColor: '#fafafa' }}
                                    >
                                      <td colSpan={8} style={{ padding: '7px 12px 7px 36px', borderBottom: '1px solid #e8e8e8' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                          <span style={{ fontSize: '11px', color: '#999', fontFamily: 'monospace', width: '14px' }}>{pacCollapsed ? '\u25B6' : '\u25BC'}</span>
                                          <span style={{ fontSize: '13px', fontWeight: '600', color: '#444' }}>{pac.paciente}</span>
                                          <span style={{ fontSize: '12px', color: '#888' }}>({pac.itens.length})</span>
                                          <span style={{ fontSize: '11px', color: '#888', marginLeft: 'auto' }}>
                                            {formatCurrency(pac.totalXML)} | {formatCurrency(pac.totalDB)}
                                          </span>
                                        </div>
                                      </td>
                                    </tr>
                                    {!pacCollapsed && pac.itens.map((item, idx) => (
                                      <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f8f8f8' }}>
                                        <td style={styles.td}>{item.numeroGuia}</td>
                                        <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.codigoProcedimento}>{item.codigoProcedimento}</td>
                                        <td style={{ ...styles.td, fontSize: '12px', wordBreak: 'break-word' }}>
                                          {item.descricaoProcedimento}
                                        </td>
                                        <td style={styles.td}>{item.protocolo}</td>
                                        <td style={styles.tdRight}>{formatCurrency(item.valorXML)}</td>
                                        <td style={styles.tdRight}>{formatCurrency(item.valorDB)}</td>
                                        <td style={{ ...styles.tdRight, color: item.diferenca > 0.01 ? '#2e7d32' : item.diferenca < -0.01 ? '#c62828' : '#333', fontWeight: Math.abs(item.diferenca) > 0.01 ? '600' : '400' }}>
                                          {formatCurrency(item.diferenca)}
                                        </td>
                                        <td style={styles.td}>{formatDate(item.dataRealizacao)}</td>
                                      </tr>
                                    ))}
                                  </React.Fragment>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div style={styles.tableInfo}>
                    Exibindo {itensFiltrados.length} de {resultado.itens.length} itens
                  </div>
                </div>

                {/* Legenda dos status */}
                <div style={{ ...styles.section, marginTop: '20px' }}>
                  <h3 style={{ ...styles.sectionTitle, fontSize: '14px' }}>Legenda dos status</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '10px' }}>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                      <span style={{ ...styles.badge, backgroundColor: '#e8f5e9', color: '#2e7d32', flexShrink: 0 }}>OK</span>
                      <span style={{ fontSize: '12px', color: '#555' }}>Exame encontrado no sistema com o mesmo valor. Conferencia perfeita.</span>
                    </div>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                      <span style={{ ...styles.badge, backgroundColor: '#fff3e0', color: '#e65100', flexShrink: 0 }}>Valor divergente</span>
                      <span style={{ fontSize: '12px', color: '#555' }}>Exame encontrado, mas o valor pago pela UNIMED difere do valor no sistema. Verificar tabela de precos.</span>
                    </div>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                      <span style={{ ...styles.badge, backgroundColor: '#ffebee', color: '#c62828', flexShrink: 0 }}>N/encontrado</span>
                      <span style={{ fontSize: '12px', color: '#555' }}>A UNIMED cobrou este exame, mas o sistema nao encontrou correspondencia (data ou paciente nao batem).</span>
                    </div>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                      <span style={{ ...styles.badge, backgroundColor: '#e3f2fd', color: '#1565c0', flexShrink: 0 }}>N/cobrado UNIMED</span>
                      <span style={{ fontSize: '12px', color: '#555' }}>Exame existe no sistema dentro do periodo, mas a UNIMED nao incluiu no pagamento. Pode ser glosa ou sera incluido em outro demonstrativo.</span>
                    </div>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                      <span style={{ ...styles.badge, backgroundColor: '#f3e5f5', color: '#7b1fa2', flexShrink: 0 }}>Fora do periodo</span>
                      <span style={{ fontSize: '12px', color: '#555' }}>Exame existe no sistema mas com data fora do periodo do demonstrativo (ex: Dez ou Fev num demonstrativo de Janeiro). E esperado nao estar incluido - aparece apenas porque a busca usa margem de +/-5 dias.</span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ConferenciaConvenios;
